import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, Marker, useMap, useMapEvents } from 'react-leaflet';
import BaseTileLayer from '../components/BaseTileLayer';
import L from 'leaflet';
import { Briefcase, ClipboardList, Anchor, Users, Award, MapPin, Clock, SlidersHorizontal, UserCheck, Building2, AlertTriangle, Bell, Moon, Wine, Package } from 'lucide-react';
import FluentEmoji from '../components/FluentEmoji';
import AvatarEditor from '../components/AvatarEditor';
import * as api from '../services/api';
import { OvertimeRulesContent } from './OvertimeRulesPage';
import { useSearchParams } from 'react-router-dom';
import { hasFeature } from '../utils/featureFlags';
import LaborCategoriesTab, { type LaborCategory, LaborCategoryBadge } from '../components/LaborCategoriesTab';

// ─── 都道府県センター座標 ──────────────────────────────────────────────────────
const PREFECTURES = [
  { name: '北海道', lat: 43.06, lng: 141.35 }, { name: '青森県', lat: 40.82, lng: 140.74 },
  { name: '岩手県', lat: 39.70, lng: 141.15 }, { name: '宮城県', lat: 38.27, lng: 140.87 },
  { name: '秋田県', lat: 39.72, lng: 140.10 }, { name: '山形県', lat: 38.24, lng: 140.36 },
  { name: '福島県', lat: 37.75, lng: 140.47 }, { name: '茨城県', lat: 36.34, lng: 140.45 },
  { name: '栃木県', lat: 36.57, lng: 139.88 }, { name: '群馬県', lat: 36.39, lng: 139.06 },
  { name: '埼玉県', lat: 35.86, lng: 139.65 }, { name: '千葉県', lat: 35.61, lng: 140.12 },
  { name: '東京都', lat: 35.69, lng: 139.69 }, { name: '神奈川県', lat: 35.45, lng: 139.64 },
  { name: '新潟県', lat: 37.90, lng: 139.02 }, { name: '富山県', lat: 36.70, lng: 137.21 },
  { name: '石川県', lat: 36.59, lng: 136.63 }, { name: '福井県', lat: 36.07, lng: 136.22 },
  { name: '山梨県', lat: 35.66, lng: 138.57 }, { name: '長野県', lat: 36.65, lng: 138.18 },
  { name: '岐阜県', lat: 35.39, lng: 136.72 }, { name: '静岡県', lat: 34.98, lng: 138.38 },
  { name: '愛知県', lat: 35.18, lng: 136.91 }, { name: '三重県', lat: 34.73, lng: 136.51 },
  { name: '滋賀県', lat: 35.00, lng: 135.87 }, { name: '京都府', lat: 35.02, lng: 135.76 },
  { name: '大阪府', lat: 34.69, lng: 135.50 }, { name: '兵庫県', lat: 34.69, lng: 135.18 },
  { name: '奈良県', lat: 34.69, lng: 135.83 }, { name: '和歌山県', lat: 34.23, lng: 135.17 },
  { name: '鳥取県', lat: 35.50, lng: 134.24 }, { name: '島根県', lat: 35.47, lng: 133.05 },
  { name: '岡山県', lat: 34.66, lng: 133.93 }, { name: '広島県', lat: 34.40, lng: 132.46 },
  { name: '山口県', lat: 34.19, lng: 131.47 }, { name: '徳島県', lat: 34.07, lng: 134.56 },
  { name: '香川県', lat: 34.34, lng: 134.04 }, { name: '愛媛県', lat: 33.84, lng: 132.77 },
  { name: '高知県', lat: 33.56, lng: 133.53 }, { name: '福岡県', lat: 33.61, lng: 130.42 },
  { name: '佐賀県', lat: 33.25, lng: 130.30 }, { name: '長崎県', lat: 32.75, lng: 129.88 },
  { name: '熊本県', lat: 32.79, lng: 130.74 }, { name: '大分県', lat: 33.24, lng: 131.61 },
  { name: '宮崎県', lat: 31.91, lng: 131.42 }, { name: '鹿児島県', lat: 31.56, lng: 130.56 },
  { name: '沖縄県', lat: 26.21, lng: 127.68 },
];

const pinIcon = L.divIcon({
  html: '<svg width="24" height="34" viewBox="0 0 24 34"><path d="M12 0C5.4 0 0 5.4 0 12c0 8.5 12 22 12 22S24 20.5 24 12C24 5.4 18.6 0 12 0z" fill="#dc2626" stroke="white" stroke-width="1.5"/><circle cx="12" cy="12" r="5" fill="white"/></svg>',
  className: '',
  iconSize: [24, 34],
  iconAnchor: [12, 34],
});

// ─── 定数 ──────────────────────────────────────────────────────────────────
const PRESET_COLORS = ['#0057d9','#6366f1','#059669','#dc2626','#d97706','#7c3aed','#0891b2','#be123c'];
const PRESET_EMOJIS_WORK = ['🚢','📦','👁','🔧','⚓','🧹','📋','📝','⛽','🛢','🔩','📡','🏗️','🚿','💡','🛟'];
const PRESET_EMOJIS_ACT  = ['🧹','🔧','📋','⛽','🛢','🔩','🌊','⚓','🚢','📦','🛡','⚙️','🔍','📝'];

// ─── 型定義 ─────────────────────────────────────────────────────────────────
interface WorkTypeMaster {
  id: string;
  name: string;
  emoji: string;
  color: string;
  requires_alcohol: number;
  description?: string;
  display_order: number;
  is_active: number;
  /** 労働区分コード（通常業務/臨時作業/安全臨時労働/非常作業/食事…）。機能フラグ labor_categories が有効な会社で使う */
  labor_category?: string | null;
}

interface ActivityMaster {
  id: string;
  name: string;
  emoji: string;
  color: string;
  description?: string;
  display_order: number;
  is_active: number;
}

interface VesselTypeMaster {
  id: string;
  name: string;
  description?: string;
  display_order: number;
  is_active: number;
}

interface VesselItem {
  id: string;
  vessel_name: string;
  image_path?: string | null;
}

interface PositionMaster {
  id: string;
  name: string;
  description?: string;
  display_order: number;
  is_active: number;
}

interface LicenseGradeMaster {
  id: string;
  name: string;
  description?: string;
  display_order: number;
  is_active: number;
}

interface PortMaster {
  id: string;
  name: string;
  code?: string;
  latitude: number;
  longitude: number;
  description?: string;
  display_order: number;
  is_active: number;
}

interface IncidentCategoryMaster {
  id: string;
  company_id: string;
  name: string;
  color: string;
  display_order: number;
  is_active: number;
  created_at: string;
}

// ─── マスタ管理のグループ定義 ────────────────────────────────────────────────
const MASTER_GROUPS = [
  {
    key: 'operations',
    label: '業務マスタ',
    color: '#0057d9',
    bg: '#eff6ff',
    items: [
      { key: 'work',     label: '業務・作業マスタ', Icon: Briefcase },
    ],
  },
  {
    key: 'vessel',
    label: '船舶マスタ',
    color: '#0891b2',
    bg: '#ecfeff',
    items: [
      { key: 'vessel-type', label: '船舶種類マスタ', Icon: Anchor },
      { key: 'port',        label: '寄港地マスタ',   Icon: MapPin },
    ],
  },
  {
    key: 'crew',
    label: '乗組員マスタ',
    color: '#7c3aed',
    bg: '#f5f3ff',
    items: [
      { key: 'position', label: '役職マスタ',   Icon: Users },
      { key: 'license',  label: '免許資格マスタ', Icon: Award },
    ],
  },
  {
    key: 'attendance',
    label: '勤怠設定',
    color: '#059669',
    bg: '#f0fdf4',
    items: [
      { key: 'overtime',   label: '残業ルール',    Icon: Clock             },
      { key: 'thresholds', label: '勤怠閾値設定',  Icon: SlidersHorizontal },
      { key: 'rest',       label: '休憩管理',      Icon: Moon              },
      { key: 'alcohol',    label: 'アルコール再検査', Icon: Wine            },
      { key: 'alcohol-workflow', label: 'アルコール測定の運用', Icon: Bell, flag: 'alcohol_linked_attendance' },
      { key: 'labor-categories', label: '労働区分（別枠・労働外）', Icon: ClipboardList, flag: 'labor_categories' },
    ],
  },
  {
    key: 'alert',
    label: 'アラート設定',
    color: '#e11d48',
    bg: '#fff1f2',
    items: [
      { key: 'alert-severity', label: 'アラート重大度・有効設定', Icon: Bell },
    ],
  },
  {
    key: 'certificate',
    label: '証明書マスタ',
    color: '#d97706',
    bg: '#fffbeb',
    items: [
      { key: 'certifier',       label: '証明者マスタ',   Icon: UserCheck    },
      { key: 'workplace',       label: '勤務先マスタ',   Icon: Building2    },
      { key: 'report-category', label: '届出区分マスタ', Icon: ClipboardList },
      { key: 'cert-options',    label: '証明書の入力候補', Icon: Package, flag: 'extra_cert_forms' },
    ],
  },
  {
    key: 'safety',
    label: '安全管理',
    color: '#dc2626',
    bg: '#fef2f2',
    items: [
      { key: 'incident-category', label: 'ヒヤリハットカテゴリマスタ', Icon: AlertTriangle },
    ],
  },
  {
    key: 'notification',
    label: '通知設定',
    color: '#0ea5e9',
    bg: '#f0f9ff',
    items: [
      { key: 'notification', label: '未ログイン検知通知', Icon: Bell },
    ],
  },
] as const;

type TabKey = 'work' | 'vessel-type' | 'position' | 'license' | 'port' | 'overtime' | 'thresholds' | 'rest' | 'alcohol' | 'alcohol-workflow' | 'labor-categories' | 'alert-severity' | 'certifier' | 'workplace' | 'report-category' | 'cert-options' | 'incident-category' | 'notification';

/** 機能フラグが付いた項目は、その機能を使う会社にだけメニューを出す */
const itemVisible = (item: { key: string }): boolean => {
  const flag = (item as { flag?: string }).flag;
  return !flag || hasFeature(flag);
};

// ─── メインページ ─────────────────────────────────────────────────────────
export default function MasterPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') ?? 'work';
  const [tab, setTab] = useState<TabKey>(initialTab as TabKey);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const g = MASTER_GROUPS.find(g => g.items.some(i => i.key === initialTab));
    return new Set([g?.key ?? 'operations']);
  });

  const currentGroup = MASTER_GROUPS.find(g => g.items.some(i => i.key === tab)) ?? MASTER_GROUPS[0]!;
  const currentItem  = currentGroup.items.find(i => i.key === tab) ?? currentGroup.items[0]!;

  const toggleGroup = (key: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div style={{ fontFamily: "'Inter','Hiragino Sans',sans-serif", display: 'flex', gap: 0, height: '100%', minHeight: 'calc(100vh - 48px)' }}>
      {/* ── 左サイドバー ── */}
      <div style={{ width: 220, flexShrink: 0, background: '#fff', borderRight: '1px solid #e2e8f0', padding: '20px 0', overflowY: 'auto' }}>
        <div style={{ padding: '0 16px 12px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          マスタ管理
        </div>
        {MASTER_GROUPS.map(g => {
          const isOpen   = openGroups.has(g.key);
          const hasActive = g.items.some(i => i.key === tab);
          return (
            <div key={g.key}>
              {/* グループヘッダー */}
              <button
                onClick={() => toggleGroup(g.key)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 16px', border: 'none', cursor: 'pointer', fontSize: 13,
                  fontWeight: hasActive ? 700 : 600,
                  color: hasActive ? g.color : '#374151',
                  background: hasActive && !isOpen ? `${g.color}10` : 'transparent',
                  transition: 'background 0.1s',
                }}>
                <span>{g.label}</span>
                <span style={{ fontSize: 10, color: '#9ca3af', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
              </button>
              {/* アイテムリスト（展開時） */}
              {isOpen && (
                <div style={{ paddingBottom: 4 }}>
                  {g.items.filter(itemVisible).map(item => {
                    const active = tab === item.key;
                    return (
                      <button key={item.key}
                        onClick={() => setTab(item.key as TabKey)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 16px 7px 28px', border: 'none', cursor: 'pointer',
                          fontSize: 12, fontWeight: active ? 700 : 400,
                          color: active ? g.color : '#4b5563',
                          background: active ? g.bg : 'transparent',
                          borderRight: active ? `3px solid ${g.color}` : '3px solid transparent',
                          transition: 'background 0.1s, color 0.1s',
                          textAlign: 'left',
                        }}>
                        <item.Icon size={13} strokeWidth={active ? 2.2 : 1.8} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── メインコンテンツ ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', background: '#f8fafc' }}>
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 4, height: 22, background: currentGroup.color, borderRadius: 2 }} />
          <div>
            <div style={{ fontSize: 11, color: currentGroup.color, fontWeight: 600 }}>{currentGroup.label}</div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#0f172a' }}>{currentItem.label}</h2>
          </div>
        </div>

        {tab === 'work'            && <WorkTypeMasterTab />}
        {tab === 'vessel-type'     && <VesselTypeMasterTab />}
        {tab === 'position'        && <PositionMasterTab />}
        {tab === 'license'         && <LicenseGradeMasterTab />}
        {tab === 'port'            && <PortMasterTab />}
        {tab === 'overtime'        && <OvertimeRulesContent />}
        {tab === 'thresholds'      && <AttendanceThresholdTab />}
        {tab === 'rest'            && <RestRulesTab />}
        {tab === 'alcohol'         && <AlcoholRecheckTab />}
        {tab === 'alcohol-workflow' && <AlcoholWorkflowSettingsTab />}
        {tab === 'labor-categories' && <LaborCategoriesTab />}
        {tab === 'alert-severity'  && <AlertSeverityTab />}
        {tab === 'certifier'       && <CertifierMasterTab />}
        {tab === 'workplace'       && <WorkplaceMasterTab />}
        {tab === 'report-category' && <ReportCategoryMasterTab />}
        {tab === 'cert-options'    && <CertOptionMasterTab />}
        {tab === 'incident-category' && <IncidentCategoryMasterTab />}
        {tab === 'notification'      && <NotificationSettingsTab />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 通知設定: 未ログイン船員の検知しきい値（設定ページから移設）
// ═══════════════════════════════════════════════════════════════════════════
function NotificationSettingsTab() {
  const [companyDays, setCompanyDays] = useState(7);
  const [vessels, setVessels]   = useState<{ id: string; vessel_name: string }[]>([]);
  const [overrides, setOverrides] = useState<Record<string, { id: string; value: number }>>({});
  const [vInput, setVInput]     = useState<Record<string, string>>({});
  const [loading, setLoading]   = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [fb, setFb]             = useState<{ ok: boolean; msg: string } | null>(null);

  const load = async () => {
    try {
      const [settingsRes, vesselsRes] = await Promise.all([
        api.get<{ data: { company: any[]; vessel: any[] } }>('/alert-settings'),
        api.get<{ data: any[] }>('/vessels'),
      ]);
      const comp = (settingsRes?.data?.company ?? []).find((s: any) => s.alert_type === 'inactive_crew');
      if (comp) setCompanyDays(comp.threshold_value);
      const ov: Record<string, { id: string; value: number }> = {};
      const vin: Record<string, string> = {};
      for (const s of (settingsRes?.data?.vessel ?? [])) {
        if (s.alert_type === 'inactive_crew' && s.vessel_id) {
          ov[s.vessel_id] = { id: s.id, value: s.threshold_value };
          vin[s.vessel_id] = String(s.threshold_value);
        }
      }
      setOverrides(ov); setVInput(vin);
      setVessels((vesselsRes?.data ?? []).map((v: any) => ({ id: v.id, vessel_name: v.vessel_name })));
    } catch (err) { console.error('[NotificationSettingsTab]', err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flash = (ok: boolean, msg: string) => { setFb({ ok, msg }); setTimeout(() => setFb(null), 2500); };

  const saveCompany = async () => {
    setSavingKey('company');
    try {
      await api.put('/alert-settings', { scope: 'company', alert_type: 'inactive_crew', threshold_value: companyDays, severity: 'warning' });
      flash(true, '会社の既定値を保存しました');
    } catch (e: any) { flash(false, e?.message ?? '保存に失敗しました'); }
    finally { setSavingKey(null); }
  };

  const saveVessel = async (vesselId: string) => {
    const raw = (vInput[vesselId] ?? '').trim();
    setSavingKey(vesselId);
    try {
      if (raw === '') {
        // 空欄 = 船別の上書きを解除（会社の既定値を使用）
        const ex = overrides[vesselId];
        if (ex) await api.del(`/alert-settings/${ex.id}`);
        flash(true, '船別設定を解除しました（会社の既定値を使用）');
      } else {
        const val = Math.max(1, Math.min(365, Number(raw)));
        await api.put('/alert-settings', { scope: 'vessel', vessel_id: vesselId, alert_type: 'inactive_crew', threshold_value: val, severity: 'warning' });
        flash(true, '船別設定を保存しました');
      }
      await load();
    } catch (e: any) { flash(false, e?.message ?? '保存に失敗しました'); }
    finally { setSavingKey(null); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  const inputStyle: React.CSSProperties = { width: 90, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', textAlign: 'center' };

  return (
    <div style={{ maxWidth: 760 }}>
      {fb && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 200,
          background: fb.ok ? '#0f172a' : '#dc2626', color: '#fff', padding: '10px 22px', borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
          {fb.msg}
        </div>
      )}

      {/* 会社の既定値 */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>未ログイン船員の通知（会社の既定値）</div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          同じ船に乗船中のメンバーが指定日数以上ログインしていない場合、船員アプリのログイン時に通知します。船別に個別設定がない船はこの既定値が使われます。
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>検知しきい値（日数）</label>
          <input type="number" min={1} max={365} value={companyDays}
            onChange={e => setCompanyDays(Math.max(1, Math.min(365, Number(e.target.value))))} style={inputStyle} />
          <span style={{ fontSize: 13, color: '#374151' }}>日以上</span>
          <button onClick={saveCompany} disabled={savingKey === 'company'}
            style={{ marginLeft: 8, padding: '9px 20px', background: savingKey === 'company' ? '#93c5fd' : '#0057d9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {savingKey === 'company' ? '保存中...' : '保存する'}
          </button>
        </div>
      </div>

      {/* 船別の個別設定 */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>船別の個別設定</div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          船ごとにしきい値を上書きできます。<strong>空欄で保存すると解除</strong>され、会社の既定値（{companyDays}日）が使われます。
        </p>
        {vessels.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>船舶が登録されていません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {vessels.map(v => {
              const hasOverride = !!overrides[v.id];
              return (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: hasOverride ? '#f0f9ff' : '#f8fafc', border: `1px solid ${hasOverride ? '#bae6fd' : '#e2e8f0'}`, borderRadius: 10 }}>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{v.vessel_name}</span>
                  {hasOverride
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', borderRadius: 999, padding: '2px 10px' }}>個別設定あり</span>
                    : <span style={{ fontSize: 11, color: '#94a3b8' }}>既定値を使用</span>}
                  <input type="number" min={1} max={365}
                    value={vInput[v.id] ?? ''} placeholder={`既定 ${companyDays}`}
                    onChange={e => setVInput(prev => ({ ...prev, [v.id]: e.target.value }))}
                    style={{ ...inputStyle, width: 96 }} />
                  <span style={{ fontSize: 13, color: '#374151' }}>日以上</span>
                  <button onClick={() => saveVessel(v.id)} disabled={savingKey === v.id}
                    style={{ padding: '7px 16px', background: savingKey === v.id ? '#93c5fd' : '#0057d9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                    {savingKey === v.id ? '…' : '保存'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 1: 業務・作業マスタ
// 以前は「業務種別マスタ」と「特別作業マスタ」に分かれていたが、同じ船の作業なのに
// 2つを別々に登録する必要があり食い違いが起きていたため1本に統合した。
// ここで登録した内容は、打刻の業務選択と「作業を追加」の両方の選択肢になる
// （サーバー側 lib/workTypeSync.ts が特別作業側のマスタへ同じ内容を書き写す）。
// ═══════════════════════════════════════════════════════════════════════════
// ─── マスタ一覧の簡易ソート（この画面のインラインテーブル用）──────────────
type SortAccessors<T> = Record<string, (r: T) => string | number | null | undefined>;
interface SortState<T> { rows: T[]; key: string | null; dir: 'asc' | 'desc'; toggle: (k: string) => void; }

function useSimpleSort<T>(rows: T[], accessors: SortAccessors<T>, defaultKey?: string, defaultDir: 'asc' | 'desc' = 'asc'): SortState<T> {
  const [key, setKey] = useState<string | null>(defaultKey ?? null);
  const [dir, setDir] = useState<'asc' | 'desc'>(defaultDir);
  const toggle = (k: string) => {
    if (key === k) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setKey(k); setDir('asc'); }
  };
  const sorted = useMemo(() => {
    const acc = key ? accessors[key] : undefined;
    if (!acc) return rows;
    return [...rows].sort((a, b) => {
      const va = acc(a) ?? ''; const vb = acc(b) ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
      const sa = String(va); const sb = String(vb);
      return dir === 'asc' ? sa.localeCompare(sb, 'ja') : sb.localeCompare(sa, 'ja');
    });
  }, [rows, key, dir]); // eslint-disable-line react-hooks/exhaustive-deps
  return { rows: sorted, key, dir, toggle };
}

/**
 * ドラッグ＆ドロップで並び替えるための小さなフック。
 * 行に付ける props を返すだけで、保存は呼び出し側が onDrop で行う。
 * ライブラリを足さずに済むよう HTML5 の drag イベントだけで作っている。
 */
function useDragReorder<T extends { id: string }>(rows: T[], onReorder: (next: T[]) => void) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const move = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const from = rows.findIndex(r => r.id === fromId);
    const to = rows.findIndex(r => r.id === toId);
    if (from < 0 || to < 0) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onReorder(next);
  };

  /** 上下ボタン用（スマホ・タブレットではドラッグしづらいため併設する） */
  const nudge = (id: string, dir: -1 | 1) => {
    const i = rows.findIndex(r => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onReorder(next);
  };

  const rowProps = (id: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; },
    onDragEnter: () => setOverId(id),
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDrop: (e: React.DragEvent) => { e.preventDefault(); if (dragId) move(dragId, id); setDragId(null); setOverId(null); },
    onDragEnd: () => { setDragId(null); setOverId(null); },
  });

  return { rowProps, nudge, dragId, overId };
}

/** 並び替えのつまみ（≡）と上下ボタン */
function DragHandleCell({ onUp, onDown, disabled, index, total }: {
  onUp: () => void; onDown: () => void; disabled?: boolean; index: number; total: number;
}) {
  const btn = (enabled: boolean) => ({
    border: '1px solid #e2e8f0', background: '#fff', borderRadius: 5, cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: 10, lineHeight: 1, padding: '2px 5px', color: enabled ? '#475569' : '#cbd5e1',
  } as React.CSSProperties);
  return (
    <td style={{ padding: '12px 8px 12px 16px', whiteSpace: 'nowrap' }}>
      <span title={disabled ? '並び替えるには「並び」の見出しをクリックしてください' : 'つかんで上下に動かせます'}
        style={{ cursor: disabled ? 'default' : 'grab', color: disabled ? '#cbd5e1' : '#94a3b8', fontSize: 15, marginRight: 6, userSelect: 'none' }}>≡</span>
      <button onClick={onUp} disabled={disabled || index === 0} style={btn(!disabled && index > 0)} title="ひとつ上へ">▲</button>
      <button onClick={onDown} disabled={disabled || index === total - 1} style={{ ...btn(!disabled && index < total - 1), marginLeft: 3 }} title="ひとつ下へ">▼</button>
    </td>
  );
}

// マスタテーブルのヘッダセル。k を渡すとソート可能（クリックで昇順/降順）。
function SortTh<T>({ label, k, s, align = 'left' }: { label: string; k?: string; s?: SortState<T>; align?: 'left' | 'right' | 'center' }) {
  const sortable = !!k && !!s;
  const active = sortable && s!.key === k;
  const arrow = active ? (s!.dir === 'asc' ? ' ▲' : ' ▼') : (sortable ? ' ⇅' : '');
  return (
    <th
      onClick={sortable ? () => s!.toggle(k!) : undefined}
      style={{ padding: '12px 16px', textAlign: align, fontSize: 12, fontWeight: 700, color: active ? '#0057d9' : '#64748b', cursor: sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', userSelect: 'none' }}
    >
      {label}<span style={{ opacity: active ? 1 : 0.4 }}>{arrow}</span>
    </th>
  );
}

function WorkTypeMasterTab() {
  const [masters, setMasters] = useState<WorkTypeMaster[]>([]);
  const [vessels, setVessels] = useState<VesselItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<WorkTypeMaster | null>(null);

  // 船舶モーダル
  const [vesselModalId, setVesselModalId] = useState<string | null>(null);
  const [vesselWorkTypes, setVesselWorkTypes] = useState<any[]>([]);
  const [vesselWtLoading, setVesselWtLoading] = useState(false);
  const [showMasterPicker, setShowMasterPicker] = useState(false);
  const [vesselOrderSaving, setVesselOrderSaving] = useState(false);
  const [vesselOrderSaved, setVesselOrderSaved] = useState(false);

  // 労働区分（機能フラグ labor_categories が有効な会社だけ列と選択肢を出す）
  const laborOn = hasFeature('labor_categories');
  const [laborCats, setLaborCats] = useState<LaborCategory[]>([]);

  const fetchMasters = async () => {
    try {
      const res = await api.get<any>('/work-type-masters');
      setMasters(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [mastersRes, vesselsRes] = await Promise.all([
          api.get<any>('/work-type-masters'),
          api.get<any>('/vessels'),
        ]);
        if (laborOn) {
          try { const c = await api.get<any>('/labor-categories', { all: '1' }); setLaborCats(c?.data ?? []); } catch { /* 区分無し */ }
        }
        setMasters(mastersRes?.data ?? []);
        setVessels((vesselsRes?.data ?? vesselsRes?.vessels ?? []).map((v: any) => ({ id: v.id, vessel_name: v.vessel_name, image_path: v.image_path ?? null })));
      } catch (err) { console.error('[ActivityMasterPage]', err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const openVesselModal = async (vesselId: string) => {
    setVesselModalId(vesselId);
    setShowMasterPicker(false);
    setVesselWtLoading(true);
    try {
      const res = await api.get<any>(`/vessels/${vesselId}/work-types`);
      setVesselWorkTypes(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
    finally { setVesselWtLoading(false); }
  };

  const addFromMaster = async (vesselId: string, master: WorkTypeMaster) => {
    try {
      await api.post(`/vessels/${vesselId}/work-types`, {
        name: master.name,
        emoji: master.emoji,
        requires_alcohol: master.requires_alcohol,
      });
      const res = await api.get<any>(`/vessels/${vesselId}/work-types`);
      setVesselWorkTypes(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  /** この船だけの並び替えを保存する（打刻カードの並びが変わる） */
  const saveVesselOrder = async (next: any[]) => {
    if (!vesselModalId) return;
    setVesselWorkTypes(next);              // 画面は即座に反映
    setVesselOrderSaving(true); setVesselOrderSaved(false);
    try {
      await api.put(`/vessels/${vesselModalId}/work-types/reorder`, { ids: next.map((w: any) => w.id) });
      setVesselOrderSaved(true);
      window.setTimeout(() => setVesselOrderSaved(false), 2500);
    } catch (err: any) {
      alert(err?.message ?? '並び順の保存に失敗しました');
      const res = await api.get<any>(`/vessels/${vesselModalId}/work-types`);
      setVesselWorkTypes(res?.data ?? []);
    } finally { setVesselOrderSaving(false); }
  };

  /** この船の並びを会社の既定順（業務・作業マスタの並び）に戻す */
  const resetVesselOrder = async () => {
    if (!vesselModalId) return;
    if (!window.confirm('この船の並び順を、会社の既定順（業務・作業マスタの並び）に戻しますか？')) return;
    setVesselOrderSaving(true);
    try {
      await api.put(`/vessels/${vesselModalId}/work-types/reset-order`, {});
      const res = await api.get<any>(`/vessels/${vesselModalId}/work-types`);
      setVesselWorkTypes(res?.data ?? []);
      setVesselOrderSaved(true);
      window.setTimeout(() => setVesselOrderSaved(false), 2500);
    } catch (err: any) {
      alert(err?.message ?? '既定順への変更に失敗しました');
    } finally { setVesselOrderSaving(false); }
  };

  /** 船から業務種別を外す（マスタ自体は消さない。過去の勤怠記録もそのまま残る） */
  const removeFromVessel = async (vesselId: string, wt: { id: string; name: string }) => {
    if (!window.confirm(`この船の業務種別から「${wt.name}」を外しますか？\n（マスタと過去の記録は残ります。打刻・勤怠修正・作業の追加の選択肢から消えます）`)) return;
    try {
      await api.del(`/vessels/${vesselId}/work-types/${wt.id}`);
      const res = await api.get<any>(`/vessels/${vesselId}/work-types`);
      setVesselWorkTypes(res?.data ?? []);
    } catch (err: any) {
      alert(err?.message ?? '削除に失敗しました');
    }
  };

  const toggleActive = async (m: WorkTypeMaster) => {
    try {
      await api.put(`/work-type-masters/${m.id}`, { is_active: m.is_active ? 0 : 1 });
      fetchMasters();
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  const handleDelete = async (m: WorkTypeMaster) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try {
      await api.del(`/work-type-masters/${m.id}`);
      fetchMasters();
    } catch (err: any) {
      alert(err?.message ?? '削除に失敗しました');
    }
  };

  // ソートフックは早期returnより前で呼ぶ（Rules of Hooks）
  // 既定は「並び」（display_order）。船員アプリの打刻カードはこの順に並ぶため、
  // 管理画面でも同じ順で見えていないと並び替えの結果が確認できない。
  const s = useSimpleSort<WorkTypeMaster>(masters, {
    display_order: m => m.display_order,
    name: m => m.name,
    requires_alcohol: m => m.requires_alcohol,
    labor_category: m => m.labor_category ?? 'normal',
    is_active: m => m.is_active,
  }, 'display_order');

  // 並び替えはこの順で表示しているときだけ有効（名称順のまま動かしても意味がないため）
  const reorderable = s.key === 'display_order' && s.dir === 'asc';
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);

  /** 並び替えた結果をサーバーへ保存する。会社の既定順として全船の初期位置になる。 */
  const saveOrder = async (next: WorkTypeMaster[]) => {
    setMasters(next.map((m, i) => ({ ...m, display_order: i + 1 })));  // 画面は即座に反映
    setSavingOrder(true); setOrderSaved(false);
    try {
      await api.put('/work-type-masters/reorder', { ids: next.map(m => m.id) });
      setOrderSaved(true);
      window.setTimeout(() => setOrderSaved(false), 2500);
    } catch (err: any) {
      alert(err?.message ?? '並び順の保存に失敗しました');
      await fetchMasters();
    } finally { setSavingOrder(false); }
  };
  const drag = useDragReorder<WorkTypeMaster>(s.rows, saveOrder);
  const vesselDrag = useDragReorder<{ id: string }>(vesselWorkTypes as { id: string }[], next => void saveVesselOrder(next));

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  const vesselName = vessels.find(v => v.id === vesselModalId)?.vessel_name ?? '';
  const vesselWtNames = new Set(vesselWorkTypes.map((w: any) => w.name));

  return (
    <div>
      {/* 並び順の説明 ＋ 新規追加ボタン */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.8 }}>
          {reorderable ? (
            <>
              行をつかんで上下に動かすと<b>船員アプリの打刻カードの並び</b>が変わります（▲▼ボタンでも動かせます）。
              <br />
              ここで決めた順は<b>会社の既定</b>です。船ごとに変えたい場合は下の船舶一覧から個別に並び替えてください。
            </>
          ) : (
            <>「{s.key === 'name' ? '名称' : 'この列'}」で並べ替え中のため、ドラッグでの並び替えはできません。
              <br />並び替えるには「<b>並び</b>」の見出しをクリックしてください。</>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {savingOrder && <span style={{ fontSize: 12, color: '#64748b' }}>保存中...</span>}
          {orderSaved && <span style={{ fontSize: 12, color: '#059669', fontWeight: 700 }}>✓ 並び順を保存しました</span>}
          <button onClick={() => setShowNew(true)}
            style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#0057d9', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            ＋ 新規追加
          </button>
        </div>
      </div>

      {/* マスタ一覧テーブル */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 32 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="並び" k="display_order" s={s} />
              <SortTh label="アイコン" />
              <SortTh label="名称" k="name" s={s} />
              {laborOn && <SortTh label="労働区分" k="labor_category" s={s} />}
              <SortTh label="色" />
              <SortTh label="アルコール検査" k="requires_alcohol" s={s} />
              <SortTh label="説明" />
              <SortTh label="状態" k="is_active" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {s.rows.length === 0 && (
              <tr><td colSpan={laborOn ? 9 : 8} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ業務種別が登録されていません</td></tr>
            )}
            {s.rows.map((m, i) => (
              <tr key={m.id}
                {...(reorderable ? drag.rowProps(m.id) : {})}
                style={{
                  borderBottom: '1px solid #f1f5f9', opacity: drag.dragId === m.id ? 0.4 : (m.is_active ? 1 : 0.5),
                  background: reorderable && drag.overId === m.id && drag.dragId !== m.id ? '#eff6ff' : undefined,
                }}>
                <DragHandleCell index={i} total={s.rows.length} disabled={!reorderable}
                  onUp={() => drag.nudge(m.id, -1)} onDown={() => drag.nudge(m.id, 1)} />
                <td style={{ padding: '12px 16px' }}>
                  <FluentEmoji emoji={m.emoji} />
                </td>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{m.name}</td>
                {laborOn && (
                  <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                    <LaborCategoryBadge code={m.labor_category ?? 'normal'} cats={laborCats} />
                  </td>
                )}
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: m.color }} />
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {m.requires_alcohol
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 8px' }}>必須</span>
                    : <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                  }
                </td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.description ?? '—'}</td>
                <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', whiteSpace: 'nowrap', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.is_active ? '#f0fdf4' : '#f8fafc', color: m.is_active ? '#059669' : '#94a3b8', border: `1px solid ${m.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {m.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditTarget(m)}
                      style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>編集</button>
                    <button onClick={() => toggleActive(m)}
                      style={{ padding: '5px 12px', border: `1px solid ${m.is_active ? '#fecaca' : '#bbf7d0'}`, borderRadius: 6, background: m.is_active ? '#fef2f2' : '#f0fdf4', cursor: 'pointer', fontSize: 12, color: m.is_active ? '#dc2626' : '#059669', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {m.is_active ? '無効化' : '有効化'}
                    </button>
                    <button onClick={() => handleDelete(m)}
                      style={{ padding: '5px 12px', border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 船舶ごとの業務種別設定 */}
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>船舶ごとの業務種別設定</h3>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>各船舶で使う業務・作業を設定します（打刻の業務選択と「作業を追加」の両方に出ます）</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {vessels.map(v => (
            <div key={v.id} onClick={() => openVesselModal(v.id)}
              style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* 船舶画像（船舶管理と同じDB連携。未登録時はイニシャル表示） */}
                <AvatarEditor
                  entityId={v.id}
                  type="vessel"
                  imagePath={v.image_path}
                  displayName={v.vessel_name}
                  size={36}
                  readOnly
                  onUpdated={() => {}}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{v.vessel_name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>クリックして設定</div>
                </div>
              </div>
            </div>
          ))}
          {vessels.length === 0 && <div style={{ fontSize: 13, color: '#94a3b8', gridColumn: '1/-1' }}>船舶が登録されていません</div>}
        </div>
      </div>

      {/* 新規作成モーダル */}
      {showNew && (
        <WorkTypeMasterFormModal mode="create" onSaved={() => { setShowNew(false); fetchMasters(); }} onClose={() => setShowNew(false)} />
      )}
      {editTarget && (
        <WorkTypeMasterFormModal mode="edit" initial={editTarget} onSaved={() => { setEditTarget(null); fetchMasters(); }} onClose={() => setEditTarget(null)} />
      )}

      {/* 船舶設定モーダル */}
      {vesselModalId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{vesselName} の業務種別</div>
              <button onClick={() => setVesselModalId(null)}
                style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
            </div>

            {vesselWtLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>読み込み中...</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
                    上から順に<b>打刻画面のカード</b>として並びます。つかんで動かすか ▲▼ で入れ替えてください。
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {vesselOrderSaving && <span style={{ fontSize: 11, color: '#64748b' }}>保存中...</span>}
                    {vesselOrderSaved && <span style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>✓ 保存しました</span>}
                    <button onClick={resetVesselOrder} disabled={vesselOrderSaving}
                      title="業務・作業マスタで決めた会社の既定順に戻します"
                      style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      会社の既定順に戻す
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {vesselWorkTypes.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 13 }}>業務種別がまだ設定されていません</div>
                  )}
                  {vesselWorkTypes.map((wt: any, i: number) => (
                    <div key={wt.id}
                      {...vesselDrag.rowProps(wt.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                        background: vesselDrag.overId === wt.id && vesselDrag.dragId !== wt.id ? '#eff6ff' : '#f8fafc',
                        borderRadius: 10, border: '1px solid #e2e8f0',
                        opacity: vesselDrag.dragId === wt.id ? 0.4 : (wt.is_active === 0 ? 0.5 : 1),
                        cursor: 'grab',
                      }}>
                      <span title="つかんで上下に動かせます" style={{ color: '#94a3b8', fontSize: 15, userSelect: 'none' }}>≡</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button onClick={() => vesselDrag.nudge(wt.id, -1)} disabled={i === 0} title="ひとつ上へ"
                          style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, fontSize: 9, lineHeight: 1, padding: '2px 4px', cursor: i === 0 ? 'not-allowed' : 'pointer', color: i === 0 ? '#cbd5e1' : '#475569' }}>▲</button>
                        <button onClick={() => vesselDrag.nudge(wt.id, 1)} disabled={i === vesselWorkTypes.length - 1} title="ひとつ下へ"
                          style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, fontSize: 9, lineHeight: 1, padding: '2px 4px', cursor: i === vesselWorkTypes.length - 1 ? 'not-allowed' : 'pointer', color: i === vesselWorkTypes.length - 1 ? '#cbd5e1' : '#475569' }}>▼</button>
                      </div>
                      <FluentEmoji emoji={wt.emoji} size={28} bgColor="transparent" />
                      <span style={{ flex: 1, fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{wt.name}</span>
                      {wt.requires_alcohol === 1 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 6px' }}>アルコール必須</span>
                      )}
                      <button onClick={() => removeFromVessel(vesselModalId, wt)} title="この船から外す"
                        style={{ padding: '4px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                        削除
                      </button>
                    </div>
                  ))}
                </div>

                {/* マスタから追加 */}
                <button onClick={() => setShowMasterPicker(p => !p)}
                  style={{ width: '100%', padding: '10px', background: '#f0f9ff', border: '1.5px dashed #7dd3fc', borderRadius: 8, cursor: 'pointer', color: '#0369a1', fontSize: 13, fontWeight: 700, marginBottom: showMasterPicker ? 12 : 0 }}>
                  {showMasterPicker ? '▲ マスタ一覧を閉じる' : '＋ マスタから追加'}
                </button>

                {showMasterPicker && (
                  <div style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>追加するマスタを選択</div>
                    {masters.filter(m => m.is_active).map(m => {
                      const alreadyAdded = vesselWtNames.has(m.name);
                      return (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, marginBottom: 4, background: alreadyAdded ? '#f1f5f9' : '#fff', border: '1px solid #e2e8f0', opacity: alreadyAdded ? 0.6 : 1 }}>
                          <FluentEmoji emoji={m.emoji} size={26} bgColor="transparent" />
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{m.name}</span>
                          {alreadyAdded
                            ? <span style={{ fontSize: 11, color: '#94a3b8' }}>追加済み</span>
                            : <button onClick={() => addFromMaster(vesselModalId, m)}
                                style={{ padding: '4px 12px', border: 'none', borderRadius: 6, background: '#0057d9', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>追加</button>
                          }
                        </div>
                      );
                    })}
                    {masters.filter(m => m.is_active).length === 0 && (
                      <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 8 }}>有効なマスタがありません</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WorkTypeMasterFormModal ───────────────────────────────────────────────
function WorkTypeMasterFormModal({ mode, initial, onSaved, onClose }: {
  mode: 'create' | 'edit';
  initial?: WorkTypeMaster;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name,             setName]            = useState(initial?.name ?? '');
  const [emoji,            setEmoji]           = useState(initial?.emoji ?? '🚢');
  const [color,            setColor]           = useState(initial?.color ?? '#0057d9');
  const [requiresAlcohol,  setRequiresAlcohol] = useState(initial?.requires_alcohol ?? 0);
  const [description,      setDescription]     = useState(initial?.description ?? '');
  const [error,            setError]           = useState('');
  const [saving,           setSaving]          = useState(false);
  // 労働区分（機能フラグ labor_categories が有効な会社だけ）
  const laborOn = hasFeature('labor_categories');
  const [laborCategory,    setLaborCategory]   = useState(initial?.labor_category ?? 'normal');
  const [laborCats,        setLaborCats]       = useState<LaborCategory[]>([]);
  useEffect(() => {
    if (!laborOn) return;
    api.get<any>('/labor-categories').then(r => setLaborCats(r?.data ?? [])).catch(() => setLaborCats([]));
  }, [laborOn]);

  const handleSave = async () => {
    if (!name.trim()) { setError('名称を入力してください'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name, emoji, color, requires_alcohol: requiresAlcohol, description: description || undefined, ...(laborOn ? { labor_category: laborCategory } : {}) };
      if (mode === 'create') {
        await api.post('/work-type-masters', payload);
      } else if (initial) {
        await api.put(`/work-type-masters/${initial.id}`, payload);
      }
      onSaved();
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{mode === 'create' ? '業務種別を追加' : '業務種別を編集'}</div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
          名称（必須）
          <input value={name} onChange={e => setName(e.target.value)} placeholder="例：接岸作業"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none' }} />
        </label>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>絵文字</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PRESET_EMOJIS_WORK.map(e => (
              <button key={e} onClick={() => setEmoji(e)}
                style={{ width: 36, height: 36, border: emoji === e ? '2px solid #0057d9' : '1px solid #e2e8f0', borderRadius: 8, background: emoji === e ? '#eff6ff' : '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {e}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>カラー</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)}
                style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: color === c ? '3px solid #0f172a' : '2px solid transparent', cursor: 'pointer' }} />
            ))}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={requiresAlcohol === 1}
            onChange={e => setRequiresAlcohol(e.target.checked ? 1 : 0)}
            style={{ width: 16, height: 16, accentColor: '#b45309', cursor: 'pointer' }} />
          <span style={{ fontWeight: 600 }}>アルコール検査必須</span>
        </label>

        {laborOn && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
            労働区分
            <select value={laborCategory} onChange={e => setLaborCategory(e.target.value)}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#fff' }}>
              {laborCats.length === 0 && <option value="normal">通常業務</option>}
              {laborCats.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
              {(() => {
                const c = laborCats.find(x => x.code === laborCategory);
                if (!c) return '通常業務・臨時作業は労働時間として数えます。';
                const parts = [c.counts_as_work ? '労働時間に含める' : '労働時間に含めない', c.counts_toward_limit ? '上限計算に含める' : '上限計算から除外', c.permits_over_limit ? '上限超えは合法（割増）' : '', c.counts_as_rest ? '休息として扱う' : '', c.requires_reason ? '事由必須' : '', c.notify_shore ? '陸上へ通知' : ''].filter(Boolean);
                return parts.join('／');
              })()}
              {laborCategory !== 'normal' && laborCategory !== 'special' && '。この区分は打刻の業務選択には出ず、「作業を追加」から記録します。'}
            </span>
          </label>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 18 }}>
          説明（任意）
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="例：出港前の接岸・離岸作業"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none' }} />
        </label>

        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#93c5fd' : '#0057d9', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '保存中...' : mode === 'create' ? '追加する' : '更新する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 3: 船舶種類マスタ
// ═══════════════════════════════════════════════════════════════════════════
function VesselTypeMasterTab() {
  const [masters, setMasters] = useState<VesselTypeMaster[]>([]);
  const s = useSimpleSort<VesselTypeMaster>(masters, { name: m => m.name, display_order: m => m.display_order, is_active: m => m.is_active }, 'display_order');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<VesselTypeMaster | null>(null);

  const fetchMasters = async () => {
    try {
      const res = await api.get<any>('/vessel-type-masters');
      setMasters(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchMasters();
      setLoading(false);
    };
    load();
  }, []);

  const toggleActive = async (m: VesselTypeMaster) => {
    try {
      await api.put(`/vessel-type-masters/${m.id}`, { is_active: m.is_active ? 0 : 1 });
      fetchMasters();
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  const handleDelete = async (m: VesselTypeMaster) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try {
      await api.del(`/vessel-type-masters/${m.id}`);
      fetchMasters();
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      {/* 新規追加ボタン */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowNew(true)}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#0891b2', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規追加
        </button>
      </div>

      {/* マスタ一覧テーブル */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="名称" k="name" s={s} />
              <SortTh label="説明" />
              <SortTh label="並び順" k="display_order" s={s} />
              <SortTh label="状態" k="is_active" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {masters.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ船舶種類が登録されていません</td></tr>
            )}
            {s.rows.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>🚢 {m.name}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.description ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>{m.display_order}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.is_active ? '#f0fdf4' : '#f8fafc', color: m.is_active ? '#059669' : '#94a3b8', border: `1px solid ${m.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {m.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditTarget(m)}
                      style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 600 }}>編集</button>
                    <button onClick={() => toggleActive(m)}
                      style={{ padding: '5px 12px', border: `1px solid ${m.is_active ? '#fecaca' : '#bbf7d0'}`, borderRadius: 6, background: m.is_active ? '#fef2f2' : '#f0fdf4', cursor: 'pointer', fontSize: 12, color: m.is_active ? '#dc2626' : '#059669', fontWeight: 600 }}>
                      {m.is_active ? '無効化' : '有効化'}
                    </button>
                    <button onClick={() => handleDelete(m)}
                      style={{ padding: '5px 12px', border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <VesselTypeMasterFormModal mode="create" onSaved={() => { setShowNew(false); fetchMasters(); }} onClose={() => setShowNew(false)} />
      )}
      {editTarget && (
        <VesselTypeMasterFormModal mode="edit" initial={editTarget} onSaved={() => { setEditTarget(null); fetchMasters(); }} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 4: 役職マスタ（権限設定を統合）
// ═══════════════════════════════════════════════════════════════════════════
type PermKey = { key: string; label: string; short: string; desc: string; group: 'crew' | 'admin'; gate?: boolean; cert?: boolean };
// 船員アプリ内の権限（同乗者の勤怠・アラート・書類受取）
const CREW_PERM_KEYS: PermKey[] = [
  { key: 'view_crew_attendance', label: '他船員の勤怠閲覧', short: '勤怠閲覧', desc: '同乗者の勤怠状況を見る', group: 'crew' },
  { key: 'edit_crew_attendance', label: '他船員の勤怠編集', short: '勤怠編集', desc: '同乗者の勤怠を修正する', group: 'crew' },
  { key: 'view_alerts',          label: 'アラート確認',     short: 'アラート確認', desc: '船内アラートを確認する', group: 'crew' },
  { key: 'resolve_alerts',       label: 'アラート解決',     short: 'アラート解決', desc: 'アラートを解決済みにする', group: 'crew' },
  { key: 'print_documents',      label: '書類の受取・印刷', short: '書類受取', desc: '陸上から届いた書類を表示・印刷', group: 'crew' },
];
// 陸上管理サイトの機能別権限（船長など役職に付与すると、船員でも陸上サイトへ制限付きログイン可能）
const ADMIN_PERM_KEYS: PermKey[] = [
  { key: 'admin_site',       label: '陸上管理サイトを開く', short: '陸上サイト', desc: '入口。これがONの役職だけ陸上サイトへログインできます', group: 'admin', gate: true },
  { key: 'admin_attendance', label: '勤怠',           short: '勤怠', desc: '勤怠一覧・打刻記録の閲覧/編集', group: 'admin' },
  { key: 'admin_leave',      label: '休暇管理',       short: '休暇', desc: '休暇・希望休の確認/承認', group: 'admin' },
  { key: 'admin_incidents',  label: 'ヒヤリハット',   short: 'ヒヤリ', desc: '報告の閲覧/対応', group: 'admin' },
  { key: 'admin_messages',   label: '連絡',           short: '連絡', desc: 'メッセージの送受信', group: 'admin' },
  { key: 'admin_alerts',     label: 'アラート',       short: 'アラート', desc: 'アラートの確認/解決', group: 'admin' },
  { key: 'admin_crew',       label: '乗組員・配乗',   short: '配乗', desc: '名簿・配乗計画の閲覧', group: 'admin' },
  { key: 'admin_vessels',    label: '運航・GPS',      short: '運航', desc: '船舶位置・運航状況', group: 'admin' },
  { key: 'admin_documents',  label: '書類・証明書の発行', short: '証明書発行', desc: '各種証明書の作成・発行・印刷', group: 'admin', cert: true },
  { key: 'admin_devices',    label: 'デバイス管理',   short: '端末', desc: '端末の登録/管理', group: 'admin' },
  { key: 'admin_all_vessels', label: '全船の勤怠・集計を閲覧', short: '全船閲覧', desc: 'OFFなら自分が乗船中の船のみ。ONで全船の勤怠・集計・レポートを閲覧', group: 'admin' },
];
const ALL_PERM_KEYS: PermKey[] = [...CREW_PERM_KEYS, ...ADMIN_PERM_KEYS];
const PERM_SHORT: Record<string, string> = Object.fromEntries(ALL_PERM_KEYS.map(k => [k.key, k.short]));

function PositionMasterTab() {
  const [masters, setMasters] = useState<PositionMaster[]>([]);
  const s = useSimpleSort<PositionMaster>(masters, { name: m => m.name, is_active: m => m.is_active }, 'name');
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<PositionMaster | null>(null);
  const [adminPermTarget, setAdminPermTarget] = useState<PositionMaster | null>(null);
  const [savingPerm, setSavingPerm] = useState<string | null>(null);
  // 役職ごとの利用状況（今この役職で乗船中の人・船）と、役職未登録の乗船者
  const [usage, setUsage] = useState<{ by_position: Record<string, { count: number; members: Array<{ user_name: string; vessel_name: string }> }>; missing: Array<{ user_name: string; vessel_name: string }> }>({ by_position: {}, missing: [] });
  useEffect(() => { api.get<any>('/vessels/assignments/position-usage').then(r => setUsage(r?.data ?? { by_position: {}, missing: [] })).catch(() => {}); }, []);

  const fetchAll = async () => {
    try {
      const [masterRes, permRes] = await Promise.all([
        api.get<any>('/position-masters'),
        api.get<any>('/position-permissions'),
      ]);
      setMasters(masterRes?.data ?? []);
      setPermissions(permRes?.data?.permissions ?? {});
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  useEffect(() => {
    const load = async () => { setLoading(true); await fetchAll(); setLoading(false); };
    load();
  }, []);

  const toggleActive = async (m: PositionMaster) => {
    try {
      await api.put(`/position-masters/${m.id}`, { is_active: m.is_active ? 0 : 1 });
      fetchAll();
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  const togglePerm = async (positionName: string, permKey: string, current: boolean) => {
    const saveKey = `${positionName}:${permKey}`;
    setSavingPerm(saveKey);
    try {
      await api.put('/position-permissions', { position: positionName, permission_key: permKey, is_allowed: !current });
      setPermissions(prev => {
        const next = { ...prev };
        if (!next[positionName]) next[positionName] = {};
        next[positionName] = { ...next[positionName], [permKey]: !current };
        return next;
      });
    } catch { alert('保存に失敗しました'); }
    finally { setSavingPerm(null); }
  };

  const handleDelete = async (m: PositionMaster) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try {
      await api.del(`/position-masters/${m.id}`);
      fetchAll();
    } catch (err: any) {
      alert(err?.message ?? '削除に失敗しました');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowNew(true)}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#059669', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規追加
        </button>
      </div>

      <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10, lineHeight: 1.6 }}>
        各役職に付与された権限は<strong style={{ color: '#4338ca' }}>「⚙ 権限設定」</strong>から変更できます。船員アプリ内の権限と、陸上管理サイトの閲覧・証明書発行の権限をまとめて設定します。
        権限は<strong>乗船中の配乗に登録された役職</strong>で判定されます（ユーザー管理の役職欄ではありません）。
        {usage.missing.length > 0 && (
          <span style={{ display: 'block', marginTop: 6, color: '#b91c1c', fontWeight: 800 }}>
            ⚠ 役職未登録のまま乗船中: {usage.missing.map(x => `${x.user_name}（${x.vessel_name}）`).join('、')} — 配乗シミュレーション、または画面上部の警告から役職を登録してください。
          </span>
        )}
      </div>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="役職名" k="name" s={s} />
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b' }}>説明</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b' }}>付与されている権限</th>
              <SortTh label="状態" k="is_active" s={s} />
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {masters.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ役職が登録されていません</td></tr>
            )}
            {s.rows.map(m => {
              const enabled = ALL_PERM_KEYS.filter(k => permissions[m.name]?.[k.key]);
              const u = usage.by_position[m.name];
              return (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '14px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14, whiteSpace: 'nowrap' }}>
                  👤 {m.name}
                  <div style={{ marginTop: 3, fontSize: 11, fontWeight: 600, color: u ? '#0f766e' : '#94a3b8' }} title={u ? u.members.map(x => `${x.user_name}（${x.vessel_name}）`).join('、') : ''}>
                    {u ? `乗船中 ${u.count}名: ${u.members.slice(0, 3).map(x => `${x.user_name}（${x.vessel_name}）`).join('、')}${u.count > 3 ? ' …' : ''}` : '乗船中の該当者なし'}
                  </div>
                </td>
                <td style={{ padding: '14px 16px', color: '#64748b', fontSize: 13 }}>{m.description ?? '—'}</td>
                <td style={{ padding: '12px 16px', maxWidth: 420 }}>
                  {enabled.length === 0
                    ? <span style={{ fontSize: 13, color: '#cbd5e1' }}>権限なし</span>
                    : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {enabled.map(k => (
                          <span key={k.key} style={{
                            fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                            color: k.group === 'admin' ? '#4338ca' : '#0369a1',
                            background: k.group === 'admin' ? '#eef2ff' : '#f0f9ff',
                            border: `1px solid ${k.group === 'admin' ? '#c7d2fe' : '#bae6fd'}`,
                          }}>{k.group === 'admin' ? '🖥 ' : ''}{k.short}</span>
                        ))}
                      </div>}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: m.is_active ? '#f0fdf4' : '#f8fafc', color: m.is_active ? '#059669' : '#94a3b8', border: `1px solid ${m.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {m.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', alignItems: 'center' }}>
                    <button onClick={() => setAdminPermTarget(m)} disabled={!m.is_active}
                      title="この役職の権限（船員アプリ内＋陸上サイト閲覧・証明書発行）を設定します"
                      style={{ padding: '6px 12px', border: '1px solid #c7d2fe', borderRadius: 8, background: '#eef2ff', cursor: m.is_active ? 'pointer' : 'not-allowed', fontSize: 12.5, color: '#4338ca', fontWeight: 800, whiteSpace: 'nowrap' }}>⚙ 権限設定</button>
                    <button onClick={() => setEditTarget(m)}
                      style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12.5, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>編集</button>
                    <button onClick={() => toggleActive(m)}
                      style={{ padding: '6px 12px', border: `1px solid ${m.is_active ? '#fecaca' : '#bbf7d0'}`, borderRadius: 8, background: m.is_active ? '#fef2f2' : '#f0fdf4', cursor: 'pointer', fontSize: 12.5, color: m.is_active ? '#dc2626' : '#059669', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {m.is_active ? '無効化' : '有効化'}
                    </button>
                    <button onClick={() => handleDelete(m)}
                      style={{ padding: '6px 12px', border: '1px solid #fecaca', borderRadius: 8, background: '#fef2f2', cursor: 'pointer', fontSize: 12.5, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>削除</button>
                  </div>
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <PositionMasterFormModal mode="create" onSaved={() => { setShowNew(false); fetchAll(); }} onClose={() => setShowNew(false)} />
      )}
      {editTarget && (
        <PositionMasterFormModal mode="edit" initial={editTarget} onSaved={() => { setEditTarget(null); fetchAll(); }} onClose={() => setEditTarget(null)} />
      )}
      {adminPermTarget && (
        <PositionAdminPermModal
          position={adminPermTarget}
          permissions={permissions[adminPermTarget.name] ?? {}}
          savingPerm={savingPerm}
          onToggle={togglePerm}
          onClose={() => setAdminPermTarget(null)}
        />
      )}
    </div>
  );
}

// ─── PositionAdminPermModal ────────────────────────────────────────────────
// 役職の権限をまとめて設定するモーダル。
// ① 船員アプリ内の権限（同乗者の勤怠・アラート・書類受取）
// ② 陸上管理サイトの権限（admin_site を入口に、勤怠/休暇/証明書発行 などを個別許可）
function PositionAdminPermModal({ position, permissions, savingPerm, onToggle, onClose }: {
  position: PositionMaster;
  permissions: Record<string, boolean>;
  savingPerm: string | null;
  onToggle: (positionName: string, permKey: string, current: boolean) => void;
  onClose: () => void;
}) {
  const gateOn = permissions['admin_site'] ?? false;
  const gate = ADMIN_PERM_KEYS.find(k => k.gate)!;
  const adminFeatures = ADMIN_PERM_KEYS.filter(k => !k.gate);

  const Toggle = ({ pk }: { pk: PermKey }) => {
    const allowed = permissions[pk.key] ?? false;
    const saveKey = `${position.name}:${pk.key}`;
    const dim = pk.group === 'admin' && !pk.gate && !gateOn;
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 4px', borderBottom: '1px solid #f1f5f9', opacity: dim ? 0.5 : 1 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
            {pk.cert && '📄 '}{pk.label}
          </div>
          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>{pk.desc}</div>
        </div>
        <button
          onClick={() => onToggle(position.name, pk.key, allowed)}
          disabled={savingPerm === saveKey || (dim && !allowed)}
          style={{
            width: 46, height: 26, borderRadius: 13, flexShrink: 0, marginTop: 2,
            background: allowed ? (pk.group === 'admin' ? '#4338ca' : '#0066CC') : '#D1D5DB',
            border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
            opacity: savingPerm === saveKey ? 0.6 : 1,
          }}
        >
          <span style={{ position: 'absolute', top: 3, left: allowed ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </button>
      </div>
    );
  };
  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <div style={{ fontSize: 12, fontWeight: 800, color: '#334155', letterSpacing: 0.3, margin: '18px 4px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 500, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '22px 24px 14px', borderBottom: '1px solid #eef2f7' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>⚙ 権限設定：{position.name}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                この役職の船員ができることを設定します。船員アプリ（スマホ）にも同じ権限が反映されます。
              </div>
            </div>
            <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b', flexShrink: 0 }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '4px 24px 20px', overflowY: 'auto' }}>
          {/* ① 船員アプリ内の権限 */}
          <SectionLabel>📱 船員アプリ内の権限</SectionLabel>
          {CREW_PERM_KEYS.map(pk => <Toggle key={pk.key} pk={pk} />)}

          {/* ② 陸上管理サイトの権限 */}
          <SectionLabel>🖥 陸上管理サイトの権限</SectionLabel>
          <div style={{ background: gateOn ? '#eef2ff' : '#f8fafc', border: `1px solid ${gateOn ? '#c7d2fe' : '#e2e8f0'}`, borderRadius: 12, padding: '2px 14px', margin: '4px 0 6px' }}>
            <Toggle pk={gate} />
          </div>
          {!gateOn
            ? <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', margin: '2px 4px 4px' }}>
                「陸上管理サイトを開く」をONにすると、下の項目を個別に許可できます。
              </div>
            : <div style={{ fontSize: 11.5, color: '#64748b', margin: '2px 4px 4px' }}>陸上サイトにログインしたとき、ONにした項目だけが表示されます。</div>}
          {adminFeatures.map(pk => <Toggle key={pk.key} pk={pk} />)}
        </div>

        <div style={{ padding: '12px 24px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 22px', border: 'none', borderRadius: 10, background: '#0f172a', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>完了</button>
        </div>
      </div>
    </div>
  );
}

// ─── PositionMasterFormModal ───────────────────────────────────────────────
function PositionMasterFormModal({ mode, initial, onSaved, onClose }: {
  mode: 'create' | 'edit';
  initial?: PositionMaster;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name,        setName]        = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [error,       setError]       = useState('');
  const [saving,      setSaving]      = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { setError('役職名を入力してください'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name, description: description || undefined };
      if (mode === 'create') {
        await api.post('/position-masters', payload);
      } else if (initial) {
        await api.put(`/position-masters/${initial.id}`, payload);
      }
      onSaved();
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{mode === 'create' ? '役職を追加' : '役職を編集'}</div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
          役職名（必須）
          <input value={name} onChange={e => setName(e.target.value)} placeholder="例：船長"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 18 }}>
          説明（任意）
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="例：船舶の最高責任者"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none' }} />
        </label>

        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#6ee7b7' : '#059669', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '保存中...' : mode === 'create' ? '追加する' : '更新する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 5: 免許資格マスタ
// ═══════════════════════════════════════════════════════════════════════════
function LicenseGradeMasterTab() {
  const [masters, setMasters] = useState<LicenseGradeMaster[]>([]);
  const s = useSimpleSort<LicenseGradeMaster>(masters, { name: m => m.name, display_order: m => m.display_order, is_active: m => m.is_active }, 'display_order');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<LicenseGradeMaster | null>(null);

  const fetchMasters = async () => {
    try {
      const res = await api.get<any>('/license-grade-masters');
      setMasters(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  useEffect(() => {
    const load = async () => { setLoading(true); await fetchMasters(); setLoading(false); };
    load();
  }, []);

  const toggleActive = async (m: LicenseGradeMaster) => {
    try { await api.put(`/license-grade-masters/${m.id}`, { is_active: m.is_active ? 0 : 1 }); fetchMasters(); }
    catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  const handleDelete = async (m: LicenseGradeMaster) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try { await api.del(`/license-grade-masters/${m.id}`); fetchMasters(); }
    catch (err: any) { alert(err?.message ?? '削除に失敗しました'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowNew(true)}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規追加
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="資格名" k="name" s={s} />
              <SortTh label="説明" />
              <SortTh label="並び順" k="display_order" s={s} />
              <SortTh label="状態" k="is_active" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {masters.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ資格が登録されていません</td></tr>
            )}
            {s.rows.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>📜 {m.name}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.description ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>{m.display_order}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.is_active ? '#f0fdf4' : '#f8fafc', color: m.is_active ? '#059669' : '#94a3b8', border: `1px solid ${m.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {m.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditTarget(m)} style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 600 }}>編集</button>
                    <button onClick={() => toggleActive(m)} style={{ padding: '5px 12px', border: `1px solid ${m.is_active ? '#fecaca' : '#bbf7d0'}`, borderRadius: 6, background: m.is_active ? '#fef2f2' : '#f0fdf4', cursor: 'pointer', fontSize: 12, color: m.is_active ? '#dc2626' : '#059669', fontWeight: 600 }}>
                      {m.is_active ? '無効化' : '有効化'}
                    </button>
                    <button onClick={() => handleDelete(m)} style={{ padding: '5px 12px', border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <LicenseGradeMasterFormModal mode="create" onSaved={() => { setShowNew(false); fetchMasters(); }} onClose={() => setShowNew(false)} />}
      {editTarget && <LicenseGradeMasterFormModal mode="edit" initial={editTarget} onSaved={() => { setEditTarget(null); fetchMasters(); }} onClose={() => setEditTarget(null)} />}
    </div>
  );
}

function LicenseGradeMasterFormModal({ mode, initial, onSaved, onClose }: {
  mode: 'create' | 'edit'; initial?: LicenseGradeMaster; onSaved: () => void; onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { setError('資格名を入力してください'); return; }
    setSaving(true); setError('');
    try {
      if (mode === 'create') await api.post('/license-grade-masters', { name, description: description || undefined });
      else if (initial) await api.put(`/license-grade-masters/${initial.id}`, { name, description: description || undefined });
      onSaved();
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{mode === 'create' ? '資格を追加' : '資格を編集'}</div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
          資格名（必須）
          <input value={name} onChange={e => setName(e.target.value)} placeholder="例：一級海技士（航海）"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none' }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 18 }}>
          説明（任意）
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="例：大型船の航海士資格"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none' }} />
        </label>
        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#a78bfa' : '#7c3aed', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '保存中...' : mode === 'create' ? '追加する' : '更新する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab 6: 寄港地マスタ
// ═══════════════════════════════════════════════════════════════════════════
function PortMasterTab() {
  const [masters, setMasters] = useState<PortMaster[]>([]);
  const s = useSimpleSort<PortMaster>(masters, { name: m => m.name, code: m => m.code ?? '', is_active: m => m.is_active }, 'name');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<PortMaster | null>(null);

  const fetchMasters = async () => {
    try {
      const res = await api.get<any>('/port-masters');
      setMasters(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  useEffect(() => {
    const load = async () => { setLoading(true); await fetchMasters(); setLoading(false); };
    load();
  }, []);

  const toggleActive = async (m: PortMaster) => {
    try { await api.put(`/port-masters/${m.id}`, { is_active: m.is_active ? 0 : 1 }); fetchMasters(); }
    catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  const handleDelete = async (m: PortMaster) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try { await api.del(`/port-masters/${m.id}`); fetchMasters(); }
    catch (err: any) { alert(err?.message ?? '削除に失敗しました'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowNew(true)}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#0891b2', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規追加
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="港名" k="name" s={s} />
              <SortTh label="コード" k="code" s={s} />
              <SortTh label="緯度" />
              <SortTh label="経度" />
              <SortTh label="説明" />
              <SortTh label="状態" k="is_active" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {masters.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ寄港地が登録されていません</td></tr>
            )}
            {s.rows.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>⚓ {m.name}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.code ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>{m.latitude.toFixed(4)}</td>
                <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>{m.longitude.toFixed(4)}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.description ?? '—'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.is_active ? '#f0fdf4' : '#f8fafc', color: m.is_active ? '#059669' : '#94a3b8', border: `1px solid ${m.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {m.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditTarget(m)} style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 600 }}>編集</button>
                    <button onClick={() => toggleActive(m)} style={{ padding: '5px 12px', border: `1px solid ${m.is_active ? '#fecaca' : '#bbf7d0'}`, borderRadius: 6, background: m.is_active ? '#fef2f2' : '#f0fdf4', cursor: 'pointer', fontSize: 12, color: m.is_active ? '#dc2626' : '#059669', fontWeight: 600 }}>
                      {m.is_active ? '無効化' : '有効化'}
                    </button>
                    <button onClick={() => handleDelete(m)} style={{ padding: '5px 12px', border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <PortMasterFormModal mode="create" onSaved={() => { setShowNew(false); fetchMasters(); }} onClose={() => setShowNew(false)} />}
      {editTarget && <PortMasterFormModal mode="edit" initial={editTarget} onSaved={() => { setEditTarget(null); fetchMasters(); }} onClose={() => setEditTarget(null)} />}
    </div>
  );
}

// ─── 地図ヘルパー（寄港地モーダル内） ───────────────────────────────────────
function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
  useMapEvents({ click(e) { onMapClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function FlyToTarget({ target }: { target: [number, number] | null }) {
  const map = useMap();
  const prevKey = useRef('');
  useEffect(() => {
    if (!target) return;
    const key = `${target[0].toFixed(3)},${target[1].toFixed(3)}`;
    if (key === prevKey.current) return;
    prevKey.current = key;
    map.flyTo(target, 11, { duration: 0.8 });
  }, [target, map]);
  return null;
}

// ─── PortMasterFormModal ───────────────────────────────────────────────────
function PortMasterFormModal({ mode, initial, onSaved, onClose }: {
  mode: 'create' | 'edit'; initial?: PortMaster; onSaved: () => void; onClose: () => void;
}) {
  const [name,        setName]        = useState(initial?.name ?? '');
  const [code,        setCode]        = useState(initial?.code ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [lat,         setLat]         = useState<number | null>(initial?.latitude ?? null);
  const [lng,         setLng]         = useState<number | null>(initial?.longitude ?? null);
  const [prefecture,  setPrefecture]  = useState('');
  const [flyTarget,   setFlyTarget]   = useState<[number, number] | null>(null);
  const [error,       setError]       = useState('');
  const [saving,      setSaving]      = useState(false);

  const handlePrefChange = (prefName: string) => {
    setPrefecture(prefName);
    const p = PREFECTURES.find(x => x.name === prefName);
    if (p) setFlyTarget([p.lat, p.lng]);
  };

  const handleMapClick = (clickLat: number, clickLng: number) => {
    setLat(clickLat);
    setLng(clickLng);
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('港名を入力してください'); return; }
    if (lat == null || lng == null) { setError('地図上でピンを立ててください'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name, code: code || undefined, latitude: lat, longitude: lng, description: description || undefined };
      if (mode === 'create') await api.post('/port-masters', payload);
      else if (initial) await api.put(`/port-masters/${initial.id}`, payload);
      onSaved();
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const initialCenter: [number, number] = initial ? [initial.latitude, initial.longitude] : [35.0, 136.0];
  const initialZoom = initial ? 12 : 5;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 820, maxWidth: '97vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{mode === 'create' ? '寄港地を追加' : '寄港地を編集'}</div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* 左：フォーム */}
          <div style={{ width: 260, flexShrink: 0 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
              港名（必須）
              <input value={name} onChange={e => setName(e.target.value)} placeholder="例：神戸港"
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
              港コード（任意）
              <input value={code} onChange={e => setCode(e.target.value)} placeholder="例：JPUKB"
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
              メモ（任意）
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="例：阪神港の一部"
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 16 }}>
              都道府県で絞り込む
              <select value={prefecture} onChange={e => handlePrefChange(e.target.value)}
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', background: '#fff' }}>
                <option value="">— 選択してください —</option>
                {PREFECTURES.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            </label>
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#0369a1' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>📍 地図をクリックしてピンを立てる</div>
              {lat != null && lng != null
                ? <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#0f172a' }}>
                    緯度: {lat.toFixed(5)}<br />経度: {lng.toFixed(5)}
                  </div>
                : <div style={{ color: '#94a3b8' }}>まだ選択されていません</div>
              }
            </div>
          </div>

          {/* 右：地図 */}
          <div style={{ flex: 1, height: 420, borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0', cursor: 'crosshair' }}>
            <MapContainer center={initialCenter} zoom={initialZoom} style={{ width: '100%', height: '100%' }}>
              <BaseTileLayer />
              <MapClickHandler onMapClick={handleMapClick} />
              <FlyToTarget target={flyTarget} />
              {lat != null && lng != null && (
                <Marker position={[lat, lng]} icon={pinIcon} />
              )}
            </MapContainer>
          </div>
        </div>

        {error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#67e8f9' : '#0891b2', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '保存中...' : mode === 'create' ? '追加する' : '更新する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── VesselTypeMasterFormModal ─────────────────────────────────────────────
function VesselTypeMasterFormModal({ mode, initial, onSaved, onClose }: {
  mode: 'create' | 'edit';
  initial?: VesselTypeMaster;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name,        setName]        = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [error,       setError]       = useState('');
  const [saving,      setSaving]      = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { setError('名称を入力してください'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name, description: description || undefined };
      if (mode === 'create') {
        await api.post('/vessel-type-masters', payload);
      } else if (initial) {
        await api.put(`/vessel-type-masters/${initial.id}`, payload);
      }
      onSaved();
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{mode === 'create' ? '船舶種類を追加' : '船舶種類を編集'}</div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
          名称（必須）
          <input value={name} onChange={e => setName(e.target.value)} placeholder="例：コンテナ船"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 18 }}>
          説明（任意）
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="例：コンテナを輸送する貨物船"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none' }} />
        </label>

        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#67e8f9' : '#0891b2', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '保存中...' : mode === 'create' ? '追加する' : '更新する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab: 勤怠閾値設定
// ═══════════════════════════════════════════════════════════════════════════
interface ThresholdItem {
  key: string;
  label: string;
  unit: string;
  default_value: number;
  value: number;
}

const THRESHOLD_META: Record<string, { description: string; min: number; max: number; step: number; toDisplay: (v: number) => string; fromDisplay: (v: number) => number }> = {
  // アルコールの警告・危険閾値はここにはない。「アラート」画面の閾値設定へ移設した
  // （以前はここで変えても打刻時にしか効かず、アラート一覧は別の値を見ていた）。
  max_daily_work_min:   { description: '1日の勤務時間がこの値を超えた場合にアラート対象となります（分指定）', min: 60,    max: 1440,  step: 30,   toDisplay: v => String(v),        fromDisplay: v => v },
  max_monthly_work_min: { description: '月間勤務時間がこの値を超えた場合にアラート対象となります（分指定）', min: 1200,  max: 20000, step: 60,   toDisplay: v => String(v),        fromDisplay: v => v },
  long_break_hours:     { description: '前回の業務終了からこの時間以上あくと、次の業務開始時にアルコール検査を必須化します（乗組員アプリで参照）', min: 1, max: 24, step: 1, toDisplay: v => String(v), fromDisplay: v => v },
};

function fmtThreshold(key: string, val: number): string {
  if (key === 'long_break_hours') return `${val}時間`;   // 値そのものが時間単位
  const h = Math.floor(val / 60), m = val % 60;
  return m > 0 ? `${h}時間${m}分` : `${h}時間`;
}

// ═══════════════════════════════════════════════════════════════════════════
// アルコール再検査: 業務開始時に検査を必須にする条件（会社の既定＋船ごとの上書き）
// ═══════════════════════════════════════════════════════════════════════════
interface AlcoholRule {
  vessel_id: string;
  enabled: number;
  since_test_enabled: number;
  since_test_hours: number;
  since_work_end_enabled: number;
  since_work_end_hours: number;
  since_work_start_enabled: number;
  since_work_start_hours: number;
}

const ALCOHOL_CONDITIONS: { on: keyof AlcoholRule; hours: keyof AlcoholRule; label: string; hint: string }[] = [
  { on: 'since_test_enabled',       hours: 'since_test_hours',       label: '前回のアルコール検査から',
    hint: '本人が最後に検査してからの経過時間で判定します' },
  { on: 'since_work_end_enabled',   hours: 'since_work_end_hours',   label: '前回の業務終了から',
    hint: '前回の勤務を終えてからの経過時間で判定します' },
  { on: 'since_work_start_enabled', hours: 'since_work_start_hours', label: 'いまの勤務の開始から',
    hint: '連続して勤務している時間で判定します' },
];

function AlcoholRuleEditor({ rule, onChange }: { rule: AlcoholRule; onChange: (r: AlcoholRule) => void }) {
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
        <input type="checkbox" checked={!!rule.enabled}
          onChange={e => onChange({ ...rule, enabled: e.target.checked ? 1 : 0 })}
          style={{ width: 18, height: 18, cursor: 'pointer' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>アルコールの再検査を実施する</span>
      </label>
      <div style={{ opacity: rule.enabled ? 1 : 0.45, pointerEvents: rule.enabled ? 'auto' : 'none' }}>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
          チェックした条件のうち、<b>どれか1つでも当てはまれば</b>業務開始時に検査を求めます。
        </div>
        {ALCOHOL_CONDITIONS.map(c => (
          <div key={String(c.on)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, background: rule[c.on] ? '#f8fafc' : '#fff' }}>
            <input type="checkbox" checked={!!rule[c.on]}
              onChange={e => onChange({ ...rule, [c.on]: e.target.checked ? 1 : 0 })}
              style={{ width: 17, height: 17, cursor: 'pointer', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{c.label}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.hint}</div>
            </div>
            <input type="number" min={0.5} max={720} step={0.5} value={String(rule[c.hours] ?? '')}
              onChange={e => onChange({ ...rule, [c.hours]: Number(e.target.value) })}
              style={{ width: 84, padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, textAlign: 'right' }} />
            <span style={{ fontSize: 13, color: '#475569', flexShrink: 0 }}>時間</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlcoholRecheckTab() {
  const [company, setCompany] = useState<AlcoholRule | null>(null);
  const [vessels, setVessels] = useState<{ id: string; vessel_name: string }[]>([]);
  const [overrides, setOverrides] = useState<AlcoholRule[]>([]);
  const [vesselId, setVesselId] = useState('');
  const [vesselRule, setVesselRule] = useState<AlcoholRule | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<any>('/alcohol-rules');
      setCompany(res?.data?.company ?? null);
      setVessels(res?.data?.vessels ?? []);
      setOverrides(res?.data?.overrides ?? []);
    } catch (e: any) { setError(e?.message ?? '読み込みに失敗しました'); }
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  // 船を選んだら、その船の上書き（無ければ会社の既定を複製）を編集対象にする
  useEffect(() => {
    if (!vesselId) { setVesselRule(null); return; }
    const ov = overrides.find(o => o.vessel_id === vesselId);
    setVesselRule(ov ? { ...ov } : company ? { ...company, vessel_id: vesselId } : null);
  }, [vesselId, overrides, company]);

  const save = async (rule: AlcoholRule, label: string) => {
    setSaving(true); setError(''); setSaved('');
    try {
      await api.put('/alcohol-rules', rule);
      setSaved(label);
      setTimeout(() => setSaved(''), 2500);
      await load();
    } catch (e: any) { setError(e?.message ?? '保存に失敗しました'); }
    setSaving(false);
  };

  const removeOverride = async (vid: string) => {
    setSaving(true); setError('');
    try { await api.del(`/alcohol-rules/${vid}`); await load(); setVesselId(''); }
    catch (e: any) { setError(e?.message ?? '削除に失敗しました'); }
    setSaving(false);
  };

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>読み込み中...</div>;
  if (!company) return <div style={{ color: '#ef4444', fontSize: 14, padding: '24px 0' }}>{error || '設定を取得できませんでした'}</div>;

  const box: React.CSSProperties = { border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, background: '#fff', marginBottom: 18 };
  const btn: React.CSSProperties = { padding: '9px 20px', border: 'none', borderRadius: 9, background: '#0057d9', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };

  return (
    <div>
      <div style={{ fontSize: 12.5, color: '#475569', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 14px', marginBottom: 16, lineHeight: 1.8 }}>
        業務開始（出勤）の打刻のときに、条件を満たしていればアルコール検査を求めます。<br />
        判定はサーバーで<b>本人の記録</b>から行うため、船内の共有端末を複数人で使っても取り違えは起きません。<br />
        業務種別ごとの「アルコール必須」（例：当直）は従来どおり有効で、この設定と<b>どちらか</b>に当てはまれば検査になります。
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {saved && <div style={{ color: '#059669', fontSize: 13, marginBottom: 12, fontWeight: 700 }}>{saved}を保存しました</div>}

      <div style={box}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>会社の既定</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14 }}>船ごとの設定が無い船は、すべてこの内容で判定します。</div>
        <AlcoholRuleEditor rule={company} onChange={setCompany} />
        <button onClick={() => void save({ ...company, vessel_id: '' }, '会社の既定')} disabled={saving} style={{ ...btn, marginTop: 14, opacity: saving ? 0.6 : 1 }}>
          {saving ? '保存中…' : '会社の既定を保存'}
        </button>
      </div>

      <div style={box}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>船ごとの上書き</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14 }}>基準を変えたい船だけ設定します。設定していない船は会社の既定のままです。</div>

        {overrides.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {overrides.map(o => {
              const v = vessels.find(x => x.id === o.vessel_id);
              return (
                <span key={o.vessel_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, color: '#1e40af' }}>
                  {v?.vessel_name ?? o.vessel_id}
                  <span style={{ fontWeight: 500, color: '#3b82f6' }}>{o.enabled ? '実施する' : '実施しない'}</span>
                  <button onClick={() => void removeOverride(o.vessel_id)} title="会社の既定に戻す"
                    style={{ border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                </span>
              );
            })}
          </div>
        )}

        <select value={vesselId} onChange={e => setVesselId(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 9, fontSize: 14, minWidth: 240, marginBottom: 14 }}>
          <option value="">-- 船を選んでください --</option>
          {vessels.map(v => (
            <option key={v.id} value={v.id}>
              {v.vessel_name}{overrides.some(o => o.vessel_id === v.id) ? '（設定あり）' : ''}
            </option>
          ))}
        </select>

        {vesselRule && (
          <>
            <AlcoholRuleEditor rule={vesselRule} onChange={setVesselRule} />
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={() => void save(vesselRule, vessels.find(v => v.id === vesselId)?.vessel_name ?? 'この船')} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>
                {saving ? '保存中…' : 'この船の設定を保存'}
              </button>
              {overrides.some(o => o.vessel_id === vesselId) && (
                <button onClick={() => void removeOverride(vesselId)} disabled={saving}
                  style={{ padding: '9px 18px', border: '1px solid #cbd5e1', borderRadius: 9, background: '#fff', color: '#475569', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
                  会社の既定に戻す
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AttendanceThresholdTab() {
  const [items, setItems]   = useState<ThresholdItem[]>([]);
  const [edits, setEdits]   = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<any>('/attendance-thresholds');
        // 休息ルール(rest_*)は「休憩管理」タブで編集するためここでは除外
        const arr: ThresholdItem[] = (res?.data ?? []).filter((it: ThresholdItem) => !it.key.startsWith('rest_'));
        setItems(arr);
        const init: Record<string, string> = {};
        for (const it of arr) init[it.key] = String(it.value);
        setEdits(init);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setError(''); setSaving(true); setSaved(false);
    try {
      const payload: Record<string, number> = {};
      for (const [k, v] of Object.entries(edits)) {
        const num = parseFloat(v);
        if (isNaN(num)) { setError(`「${items.find(i => i.key === k)?.label ?? k}」の値が不正です`); setSaving(false); return; }
        payload[k] = num;
      }
      await api.put('/attendance-thresholds', payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    setSaving(false);
  };

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>読み込み中...</div>;

  return (
    <div style={{ maxWidth: 680 }}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
        出退勤管理に関わる各種閾値を設定します。変更は即時に反映されます。
      </p>
      <p style={{ margin: '0 0 20px', fontSize: 12, color: '#94a3b8' }}>
        アルコールの警告・危険の判定値は「アラート」画面の閾値設定にまとめました。
        船ごと・船員ごとの設定もそちらで行えます。
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map(it => {
          const meta = THRESHOLD_META[it.key];
          const current = parseFloat(edits[it.key] ?? String(it.value));
          const changed = current !== it.value;
          return (
            <div key={it.key} style={{ background: '#fff', border: `1.5px solid ${changed ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{it.label}</div>
                  {meta && <div style={{ fontSize: 12, color: '#64748b' }}>{meta.description}</div>}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    デフォルト: {fmtThreshold(it.key, it.default_value)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <input
                    type="number"
                    value={edits[it.key] ?? ''}
                    min={meta?.min}
                    max={meta?.max}
                    step={meta?.step}
                    onChange={e => setEdits(prev => ({ ...prev, [it.key]: e.target.value }))}
                    style={{ width: 100, padding: '8px 10px', border: `1.5px solid ${changed ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, fontWeight: 700, textAlign: 'right', outline: 'none', color: '#0f172a' }}
                  />
                  <span style={{ fontSize: 12, color: '#64748b', minWidth: 36 }}>{it.unit}</span>
                  {!it.key.endsWith('_mgl') && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>（{fmtThreshold(it.key, current)}）</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && <div style={{ marginTop: 14, color: '#dc2626', fontSize: 12, fontWeight: 600 }}>{error}</div>}

      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '11px 28px', border: 'none', borderRadius: 10, background: saving ? '#93c5fd' : '#1d4ed8', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
          {saving ? '保存中...' : '変更を保存'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>✓ 保存しました</span>}
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, fontSize: 12, color: '#854d0e' }}>
        アルコール閾値は乗組員アプリでの打刻時に参照されます。変更後の打刻から新しい閾値が適用されます。
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab: 休憩管理（休息時間の判定ルール）
// ═══════════════════════════════════════════════════════════════════════════
const REST_RULE_META: Record<string, { description: string; min: number; max: number; step: number }> = {
  rest_24h_min_continuous_min:  { description: '直近24時間の最長連続休息がこの値未満で「違反」判定になります（MLC2006基準: 600分=10時間）', min: 60,  max: 1440,  step: 30 },
  rest_24h_warn_continuous_min: { description: '直近24時間の最長連続休息がこの値未満で「警告」判定になります（違反の一歩手前で気づくためのライン）', min: 60,  max: 1440,  step: 30 },
  rest_7d_min_total_min:        { description: '直近7日間の休息合計がこの値未満で「違反」判定になります（MLC2006基準: 4620分=77時間）', min: 600, max: 10080, step: 60 },
  rest_7d_warn_total_min:       { description: '直近7日間の休息合計がこの値未満で「警告」判定になります', min: 600, max: 10080, step: 60 },
};

function RestRulesTab() {
  const [items, setItems]   = useState<ThresholdItem[]>([]);
  const [edits, setEdits]   = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<any>('/attendance-thresholds');
        const arr: ThresholdItem[] = (res?.data ?? []).filter((it: ThresholdItem) => it.key.startsWith('rest_'));
        setItems(arr);
        const init: Record<string, string> = {};
        for (const it of arr) init[it.key] = String(it.value);
        setEdits(init);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setError(''); setSaving(true); setSaved(false);
    try {
      const payload: Record<string, number> = {};
      for (const [k, v] of Object.entries(edits)) {
        const num = parseFloat(v);
        if (isNaN(num)) { setError(`「${items.find(i => i.key === k)?.label ?? k}」の値が不正です`); setSaving(false); return; }
        payload[k] = num;
      }
      const warn24 = payload['rest_24h_warn_continuous_min'] ?? 0;
      const min24  = payload['rest_24h_min_continuous_min'] ?? 0;
      const warn7d = payload['rest_7d_warn_total_min'] ?? 0;
      const min7d  = payload['rest_7d_min_total_min'] ?? 0;
      if (warn24 < min24) { setError('24時間の警告ラインは最低連続休息以上にしてください'); setSaving(false); return; }
      if (warn7d < min7d) { setError('7日間の警告ラインは最低休息合計以上にしてください'); setSaving(false); return; }
      await api.put('/attendance-thresholds', payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    setSaving(false);
  };

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>読み込み中...</div>;

  return (
    <div style={{ maxWidth: 680 }}>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
        休息時間ページとアラートの判定に使う基準を設定します。
        海事労働条約(MLC2006)の最低基準（連続10時間 / 7日77時間）を下回る設定は監査上推奨されません。
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map(it => {
          const meta = REST_RULE_META[it.key];
          const current = parseFloat(edits[it.key] ?? String(it.value));
          const changed = current !== it.value;
          return (
            <div key={it.key} style={{ background: '#fff', border: `1.5px solid ${changed ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{it.label}</div>
                  {meta && <div style={{ fontSize: 12, color: '#64748b' }}>{meta.description}</div>}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    デフォルト: {fmtThreshold(it.key, it.default_value)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <input
                    type="number"
                    value={edits[it.key] ?? ''}
                    min={meta?.min}
                    max={meta?.max}
                    step={meta?.step}
                    onChange={e => setEdits(prev => ({ ...prev, [it.key]: e.target.value }))}
                    style={{ width: 100, padding: '8px 10px', border: `1.5px solid ${changed ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, fontWeight: 700, textAlign: 'right', outline: 'none', color: '#0f172a' }}
                  />
                  <span style={{ fontSize: 12, color: '#64748b', minWidth: 20 }}>{it.unit}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>（{fmtThreshold(it.key, current)}）</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && <div style={{ marginTop: 14, color: '#dc2626', fontSize: 12, fontWeight: 600 }}>{error}</div>}

      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '11px 28px', border: 'none', borderRadius: 10, background: saving ? '#93c5fd' : '#1d4ed8', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
          {saving ? '保存中...' : '変更を保存'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>✓ 保存しました</span>}
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, fontSize: 12, color: '#854d0e' }}>
        変更は休息時間ページの一覧・PDF、および休息不足アラートの判定に即時反映されます。
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab: アラート重大度・有効設定（各アラートを 無効/情報/警告/重大 で設定）
// ═══════════════════════════════════════════════════════════════════════════
type Severity = 'info' | 'warning' | 'critical';
type AlertKind = 'fixed' | 'threshold';
interface AlertCfgRow { alert_type: string; kind: AlertKind; enabled: boolean; severity: Severity; default_severity: Severity; }

// 全アラートの説明。「情報」に何が入るかを含め、種別を明確にする。
const ALERT_META: Record<string, { label: string; desc: string }> = {
  missing_clock_in:       { label: '出勤未打刻',         desc: '乗船中の船員が当日の出勤打刻をしていないとき（朝8時以降に検知）' },
  alcohol_skipped:        { label: 'アルコール未実施',   desc: 'アルコール検査が必須の作業で、検査せずに業務開始したとき' },
  alcohol_manual:         { label: 'アルコール手入力',   desc: '検知器を使わずアルコール値を手入力で登録したとき（測定の信頼性が下がる）' },
  alcohol_device_milestone: { label: '検知器の使用回数', desc: 'アルコール検知器の使用回数が100回の区切りに達したとき（寿命1000回の使い捨てのため）' },
  alcohol_device_limit:     { label: '検知器の交換時期', desc: 'アルコール検知器の使用回数が900回以上になったとき。寿命の1000回に達すると「重大」になります' },
  face_missing:           { label: '顔写真なし',         desc: '業務開始時に本人確認の顔写真が撮影されなかったとき' },
  device_vessel_mismatch: { label: '船舶不一致ログイン', desc: '端末に登録された船舶と異なる船員がログインを試みたとき（なりすまし・誤操作の検知）' },
  license_expiring:       { label: '海技免状の期限',     desc: '海技免状の有効期限が迫った／切れたとき（資格・期限管理の会社は種別マスタの通知段階で毎日チェック。それ以外は90日以内）' },
  seaman_book_expiring:   { label: '船員手帳の期限',     desc: '船員手帳の有効期限が迫った／切れたとき（資格・期限管理の会社は種別マスタの通知段階で毎日チェック。それ以外は90日以内）' },
  certificate_expiring:   { label: 'その他資格の期限',   desc: 'STCW基本訓練・健康証明書など、免状・手帳以外の資格の有効期限が迫った／切れたとき（資格・期限管理の種別マスタの通知段階で毎日チェック）' },
  no_clock_out:           { label: '退勤未打刻',         desc: '一定時間以上ずっと勤務中のまま（勤怠閾値設定の時間で警告→重大）' },
  overtime_exceeded:      { label: '残業超過',           desc: '当月の残業が基準を超過（勤怠閾値設定の時間で警告→重大）' },
  consecutive_work:       { label: '連続勤務',           desc: '連続勤務日数が基準を超過（勤怠閾値設定の日数で警告→重大）' },
  alcohol_detected:       { label: 'アルコール検知',     desc: '測定値が基準超過（勤怠閾値設定の値で警告→重大）' },
  rest_violation:         { label: '休息不足',           desc: 'MLC/STCWの休息基準を下回る（休憩管理の時間で警告→重大）' },
};

const SEVERITY_CHOICES: { key: Severity; label: string; color: string; bg: string; border: string }[] = [
  { key: 'info',     label: '情報', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  { key: 'warning',  label: '警告', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { key: 'critical', label: '重大', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
];
const OFF_STYLE = { color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' };

function AlertCfgCard({ r, saving, saved, onChange }: {
  r: AlertCfgRow; saving: boolean; saved: boolean;
  onChange: (alert_type: string, value: string) => void;
}) {
  const meta = ALERT_META[r.alert_type];
  if (!meta) return null;
  // fixed: [無効][情報][警告][重大] / threshold: [無効][有効(閾値判定)]
  const value = !r.enabled ? 'off' : (r.kind === 'threshold' ? 'auto' : r.severity);
  const btn = (key: string, label: string, st: { color: string; bg: string; border: string }) => {
    const active = value === key;
    return (
      <button key={key} onClick={() => onChange(r.alert_type, key)} disabled={saving}
        style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: active ? 700 : 500, cursor: 'pointer',
          border: active ? `2px solid ${st.color}` : '1.5px solid #e2e8f0',
          background: active ? st.bg : '#fff', color: active ? st.color : '#64748b' }}>
        {label}
      </button>
    );
  };
  return (
    <div style={{ background: r.enabled ? '#fff' : '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', opacity: r.enabled ? 1 : 0.85 }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{meta.label}</div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.6 }}>{meta.desc}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {btn('off', '無効', OFF_STYLE)}
        <span style={{ width: 1, height: 22, background: '#e2e8f0', margin: '0 2px' }} />
        {r.kind === 'threshold'
          ? btn('auto', '有効（閾値判定）', { color: '#0284c7', bg: '#eff6ff', border: '#bae6fd' })
          : SEVERITY_CHOICES.map(s => btn(s.key, s.label, s))}
        {saved && <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>✓</span>}
      </div>
    </div>
  );
}

function AlertSeverityTab() {
  const [rows, setRows]     = useState<AlertCfgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey]   = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<any>('/alert-settings/severity');
        setRows(res?.data ?? []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const onChange = async (alert_type: string, value: string) => {
    setRows(prev => prev.map(r => {
      if (r.alert_type !== alert_type) return r;
      if (value === 'off')  return { ...r, enabled: false };
      if (value === 'auto') return { ...r, enabled: true };
      return { ...r, enabled: true, severity: value as Severity };
    }));
    setSavingKey(alert_type); setSavedKey(null);
    try {
      await api.put('/alert-settings/severity', { alert_type, severity: value });
      setSavedKey(alert_type);
      setTimeout(() => setSavedKey(k => k === alert_type ? null : k), 2000);
    } catch (e: any) { alert(e.message ?? '保存に失敗しました'); }
    setSavingKey(null);
  };

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>読み込み中...</div>;

  const fixed = rows.filter(r => r.kind === 'fixed');
  const threshold = rows.filter(r => r.kind === 'threshold');

  return (
    <div style={{ maxWidth: 820 }}>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>
        各アラートを「情報 / 警告 / 重大」のどれで通知するか、または「無効（通知しない）」を設定します。変更は次回以降に発生するアラートへ即時反映されます。
      </p>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {[{ label: '無効', ...OFF_STYLE }, ...SEVERITY_CHOICES].map(s => (
          <span key={s.label} style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 999, fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
            {s.label}
          </span>
        ))}
        <span style={{ fontSize: 11.5, color: '#94a3b8', alignSelf: 'center' }}>← 重大度の色（アラート画面・件数バッジと共通）</span>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '4px 0 8px' }}>重大度を選べるアラート</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {fixed.map(r => (
          <AlertCfgCard key={r.alert_type} r={r} saving={savingKey === r.alert_type} saved={savedKey === r.alert_type} onChange={onChange} />
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '22px 0 8px' }}>
        閾値で警告／重大が決まるアラート（重大度は「勤怠閾値設定」「休憩管理」で調整。ここでは有効／無効のみ）
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {threshold.map(r => (
          <AlertCfgCard key={r.alert_type} r={r} saving={savingKey === r.alert_type} saved={savedKey === r.alert_type} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// アルコール測定の運用（測定連動の勤怠 alcohol_linked_attendance の会社のみ）
// 通知のタイミング・有効な測定の範囲・測定なしで終了したときの通知先・承認依頼の宛先・適用する船を
// 会社ごとに決める。サーバー側 lib/alcoholWorkflow.ts の getSettings がこの設定を読む。
// ═══════════════════════════════════════════════════════════════════════════
interface AlcoholWorkflowSettings {
  reminder_minutes: number;
  window_before_min: number;
  window_after_min: number;
  notify_self: boolean;
  notify_captain: boolean;
  notify_admins: boolean;
  notify_user_ids: string[];
  approver_user_ids: string[];
  vessel_scope: 'all' | 'selected';
  vessel_ids: string[];
}
interface SimpleUser { id: string; user_name: string; user_role: string; employee_code?: string | null; is_active?: number }

function AlcoholWorkflowSettingsTab() {
  const [s, setS] = useState<AlcoholWorkflowSettings | null>(null);
  const [vessels, setVessels] = useState<{ id: string; vessel_name: string }[]>([]);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [saving, setSaving] = useState(false);
  const [fb, setFb] = useState<{ ok: boolean; msg: string } | null>(null);
  const [userPick, setUserPick] = useState('');
  const [approverPick, setApproverPick] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [st, vs, us] = await Promise.all([
          api.get<{ data: AlcoholWorkflowSettings }>('/alcohol-workflow/settings'),
          api.get<{ data: any[] }>('/vessels'),
          api.get<{ data: SimpleUser[] }>('/users', { limit: '500' }),
        ]);
        setS(st?.data ?? null);
        setVessels((vs?.data ?? []).map((v: any) => ({ id: v.id, vessel_name: v.vessel_name })));
        setUsers((us?.data ?? []).filter(u => u.is_active !== 0));
      } catch (err) { console.error('[AlcoholWorkflowSettingsTab]', err); }
    })();
  }, []);

  const flash = (ok: boolean, msg: string) => { setFb({ ok, msg }); setTimeout(() => setFb(null), 2500); };
  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const r = await api.put<{ data: AlcoholWorkflowSettings }>('/alcohol-workflow/settings', s);
      if (r?.data) setS(r.data);
      flash(true, '保存しました（次の通知・判定から反映されます）');
    } catch (e: any) { flash(false, e?.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  if (!s) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  const admins = users.filter(u => ['shore_admin', 'system_admin'].includes(u.user_role));
  const nameOf = (id: string) => users.find(u => u.id === id)?.user_name ?? id;
  const card: React.CSSProperties = { background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 };
  const h: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 };
  const p: React.CSSProperties = { margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.6 };
  const num: React.CSSProperties = { width: 84, border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 14, outline: 'none', textAlign: 'center' };
  const sel: React.CSSProperties = { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', background: '#fff' };
  const Toggle = ({ on, label, sub, onChange }: { on: boolean; label: string; sub?: string; onChange: (v: boolean) => void }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: on ? '#f0f9ff' : '#f8fafc', border: `1px solid ${on ? '#bae6fd' : '#e2e8f0'}`, borderRadius: 10, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} style={{ width: 18, height: 18 }} />
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</span>}
      </span>
    </label>
  );
  const Chips = ({ ids, onRemove }: { ids: string[]; onRemove: (id: string) => void }) => ids.length === 0 ? null : (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {ids.map(id => (
        <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: '#0369a1', background: '#e0f2fe', borderRadius: 999, padding: '4px 10px' }}>
          {nameOf(id)}
          <button onClick={() => onRemove(id)} style={{ border: 'none', background: 'transparent', color: '#0369a1', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
        </span>
      ))}
    </div>
  );

  return (
    <div style={{ maxWidth: 820 }}>
      {fb && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 200,
          background: fb.ok ? '#0f172a' : '#dc2626', color: '#fff', padding: '10px 22px', borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
          {fb.msg}
        </div>
      )}
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
        当直などアルコール測定が必要な勤務（業務・作業マスタで「アルコール検査」をONにした業務）について、通知のタイミング・有効な測定の範囲・通知先・適用する船を決めます。
      </p>

      {/* ① 適用する船 */}
      <div style={card}>
        <div style={h}>① 適用する船</div>
        <p style={p}>この運用を使う船を選びます。選んだ船以外では、測定必須の業務でも通知・承認・自動紐付けは働きません（従来どおり打刻時の測定だけ）。</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {([['all', '全船に適用'], ['selected', '選んだ船だけに適用']] as const).map(([k, label]) => (
            <button key={k} onClick={() => setS({ ...s, vessel_scope: k })}
              style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: s.vessel_scope === k ? 700 : 500, cursor: 'pointer',
                border: s.vessel_scope === k ? '2px solid #0057d9' : '1.5px solid #e2e8f0', background: s.vessel_scope === k ? '#eff6ff' : '#fff', color: s.vessel_scope === k ? '#0057d9' : '#64748b' }}>
              {label}
            </button>
          ))}
        </div>
        {s.vessel_scope === 'selected' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
            {vessels.map(v => {
              const on = s.vessel_ids.includes(v.id);
              return <Toggle key={v.id} on={on} label={v.vessel_name} onChange={c => setS({ ...s, vessel_ids: c ? [...s.vessel_ids, v.id] : s.vessel_ids.filter(x => x !== v.id) })} />;
            })}
            {vessels.length === 0 && <div style={{ fontSize: 13, color: '#94a3b8' }}>船舶が登録されていません</div>}
          </div>
        )}
      </div>

      {/* ② 通知のタイミングと有効な測定の範囲 */}
      <div style={card}>
        <div style={h}>② 測定のお願い通知と、有効な測定の範囲</div>
        <p style={p}>出勤予定の何分前に本人へ「共有端末で測定してください」と知らせるか（連絡＋プッシュ）と、出勤予定の前後どこまでの測定を「その勤怠の測定」として扱うかを決めます。</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ width: 150, fontSize: 13, fontWeight: 600, color: '#374151' }}>測定のお願い通知</span>
            <span style={{ fontSize: 13, color: '#374151' }}>出勤予定の</span>
            <input type="number" min={0} max={1440} value={s.reminder_minutes} onChange={e => setS({ ...s, reminder_minutes: Number(e.target.value) })} style={num} />
            <span style={{ fontSize: 13, color: '#374151' }}>分前に本人へ通知</span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>（0＝通知しない）</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ width: 150, fontSize: 13, fontWeight: 600, color: '#374151' }}>有効な測定の範囲</span>
            <span style={{ fontSize: 13, color: '#374151' }}>出勤予定の</span>
            <input type="number" min={0} max={1440} value={s.window_before_min} onChange={e => setS({ ...s, window_before_min: Number(e.target.value) })} style={num} />
            <span style={{ fontSize: 13, color: '#374151' }}>分前 〜</span>
            <input type="number" min={0} max={1440} value={s.window_after_min} onChange={e => setS({ ...s, window_after_min: Number(e.target.value) })} style={num} />
            <span style={{ fontSize: 13, color: '#374151' }}>分後 の測定を自動で勤怠に紐付け</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '8px 12px', lineHeight: 1.6 }}>
            例: 「30分前 〜 30分後」なら 16:00 当直の測定は 15:30〜16:30 が有効。範囲外の測定も記録は残り、本人が勤怠を直すときに選んで付けられます。
          </div>
        </div>
      </div>

      {/* ③ 測定なしで終了したときの通知先 */}
      <div style={card}>
        <div style={h}>③ 測定なしで勤務を終えたときの通知先</div>
        <p style={p}>陸上のアラート（アラート一覧）は常に残ります。ここでは連絡・プッシュ通知を誰に送るかを決めます。</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8 }}>
          <Toggle on={s.notify_self} label="本人" sub="連絡＋プッシュ" onChange={v => setS({ ...s, notify_self: v })} />
          <Toggle on={s.notify_captain} label="船長" sub="その船に乗船中の船長へ連絡＋プッシュ" onChange={v => setS({ ...s, notify_captain: v })} />
          <Toggle on={s.notify_admins} label="陸上管理者（全員）" sub="プッシュ通知" onChange={v => setS({ ...s, notify_admins: v })} />
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>追加の宛先（任意）</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={userPick} onChange={e => setUserPick(e.target.value)} style={{ ...sel, minWidth: 260 }}>
              <option value="">— 乗組員・管理者から選ぶ —</option>
              {users.filter(u => !s.notify_user_ids.includes(u.id)).map(u => <option key={u.id} value={u.id}>{u.user_name}{u.employee_code ? `（${u.employee_code}）` : ''}</option>)}
            </select>
            <button disabled={!userPick} onClick={() => { setS({ ...s, notify_user_ids: [...s.notify_user_ids, userPick] }); setUserPick(''); }}
              style={{ padding: '8px 14px', background: userPick ? '#0057d9' : '#e2e8f0', color: userPick ? '#fff' : '#94a3b8', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: userPick ? 'pointer' : 'default' }}>追加</button>
          </div>
          <Chips ids={s.notify_user_ids} onRemove={id => setS({ ...s, notify_user_ids: s.notify_user_ids.filter(x => x !== id) })} />
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>例: 船長を外して一等航海士へ、担当の陸上管理者だけへ、など</div>
        </div>
      </div>

      {/* ④ 承認依頼の宛先 */}
      <div style={card}>
        <div style={h}>④ 事後登録・修正の承認依頼を受け取る人</div>
        <p style={p}>船員が個人アプリから測定必須の勤怠を事後に登録・修正したとき、承認依頼（連絡＋プッシュ）を送る陸上管理者を指定します。未指定なら陸上管理者全員に届きます。承認は勤怠管理の「承認待ち」から、陸上管理者なら誰でも行えます。</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={approverPick} onChange={e => setApproverPick(e.target.value)} style={{ ...sel, minWidth: 260 }}>
            <option value="">— 陸上管理者から選ぶ —</option>
            {admins.filter(u => !s.approver_user_ids.includes(u.id)).map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
          </select>
          <button disabled={!approverPick} onClick={() => { setS({ ...s, approver_user_ids: [...s.approver_user_ids, approverPick] }); setApproverPick(''); }}
            style={{ padding: '8px 14px', background: approverPick ? '#0057d9' : '#e2e8f0', color: approverPick ? '#fff' : '#94a3b8', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: approverPick ? 'pointer' : 'default' }}>追加</button>
        </div>
        {s.approver_user_ids.length === 0
          ? <div style={{ fontSize: 12.5, color: '#0369a1', marginTop: 8, fontWeight: 600 }}>現在: 陸上管理者全員（{admins.length}名）に届きます</div>
          : <Chips ids={s.approver_user_ids} onRemove={id => setS({ ...s, approver_user_ids: s.approver_user_ids.filter(x => x !== id) })} />}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={save} disabled={saving}
          style={{ padding: '10px 26px', background: saving ? '#93c5fd' : '#0057d9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {saving ? '保存中...' : 'この内容で保存する'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 証明者マスタ
// ═══════════════════════════════════════════════════════════════════════════
interface CertifierRecord {
  id: string; name: string; relation: string | null;
  address: string | null; tel: string | null;
  position: string | null; company_name: string | null;
  occupation: string | null;
  is_active: number;
}

function CertifierMasterTab() {
  const [items, setItems] = useState<CertifierRecord[]>([]);
  const s = useSimpleSort<CertifierRecord>(items, { name: m => m.name, relation: m => m.relation ?? '', position: m => m.position ?? '', company_name: m => m.company_name ?? '', tel: m => m.tel ?? '' }, 'name');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<CertifierRecord | null>(null);
  const [draft, setDraft] = useState({ name: '', relation: '', address: '', tel: '', position: '', company_name: '', occupation: '' });

  const FIELDS = [
    { k: 'name' as const, l: '氏名*' },
    { k: 'relation' as const, l: '申請者との間柄' },
    { k: 'address' as const, l: '現住所' },
    { k: 'tel' as const, l: '電話番号' },
    { k: 'occupation' as const, l: '職業' },
  ];

  const fetchItems = async () => {
    try {
      const res = await api.get<any>('/certificates/certifiers');
      setItems((res?.data ?? res) as CertifierRecord[]);
    } catch { setItems([]); }
  };

  useEffect(() => {
    const load = async () => { setLoading(true); await fetchItems(); setLoading(false); };
    load();
  }, []);

  const openNew = () => {
    setEditTarget(null);
    setDraft({ name: '', relation: '', address: '', tel: '', position: '', company_name: '', occupation: '' });
    setShowForm(true);
  };

  const openEdit = (m: CertifierRecord) => {
    setEditTarget(m);
    setDraft({ name: m.name, relation: m.relation ?? '', address: m.address ?? '', tel: m.tel ?? '', position: m.position ?? '', company_name: m.company_name ?? '', occupation: m.occupation ?? '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { alert('氏名は必須です'); return; }
    try {
      if (editTarget) {
        await api.put(`/certificates/certifiers/${editTarget.id}`, draft);
      } else {
        await api.post('/certificates/certifiers', draft);
      }
      setShowForm(false); setEditTarget(null);
      await fetchItems();
    } catch { alert('保存に失敗しました'); }
  };

  const handleDelete = async (m: CertifierRecord) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try { await api.del(`/certificates/certifiers/${m.id}`); await fetchItems(); }
    catch { alert('削除に失敗しました'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>乗船履歴証明書の証明者情報をマスタ登録します。証明書ページのプルダウンから選択できます。</p>
        <button onClick={openNew}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規追加
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="氏名" k="name" s={s} />
              <SortTh label="間柄" k="relation" s={s} />
              <SortTh label="職名" k="position" s={s} />
              <SortTh label="勤務先" k="company_name" s={s} />
              <SortTh label="電話番号" k="tel" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ証明者が登録されていません</td></tr>
            )}
            {s.rows.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>👤 {m.name}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.relation ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.position ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.company_name ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.tel ?? '—'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(m)}
                      style={{ padding: '5px 12px', border: 'none', borderRadius: 7, background: '#3b82f6', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>編集</button>
                    <button onClick={() => handleDelete(m)}
                      style={{ padding: '5px 12px', border: 'none', borderRadius: 7, background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{editTarget ? '証明者を編集' : '証明者を新規追加'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {FIELDS.map(({ k, l }) => (
                <div key={k}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 700, color: '#374151' }}>{l}</label>
                  <input type="text" value={draft[k]} onChange={e => setDraft(p => ({ ...p, [k]: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={handleSave}
                style={{ flex: 2, padding: '11px 0', border: 'none', borderRadius: 10, background: '#16a34a', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>
                保存
              </button>
              <button onClick={() => { setShowForm(false); setEditTarget(null); }}
                style={{ flex: 1, padding: '11px 0', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#374151', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 勤務先マスタ
// ═══════════════════════════════════════════════════════════════════════════
interface WorkplaceRecord {
  id: string; name: string;
  address: string | null; tel: string | null;
  position: string | null; occupation: string | null; department: string | null;
  is_active: number;
}

function WorkplaceMasterTab() {
  const [items, setItems] = useState<WorkplaceRecord[]>([]);
  const s = useSimpleSort<WorkplaceRecord>(items, { name: m => m.name, address: m => m.address ?? '', tel: m => m.tel ?? '' }, 'name');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<WorkplaceRecord | null>(null);
  const [draft, setDraft] = useState({ name: '', address: '', tel: '', position: '', occupation: '', department: '' });

  const FIELDS = [
    { k: 'name' as const, l: '勤務先名称*' },
    { k: 'address' as const, l: '所在地' },
    { k: 'tel' as const, l: '電話番号' },
    { k: 'occupation' as const, l: '職業' },
    { k: 'department' as const, l: '所属部署' },
    { k: 'position' as const, l: '職名' },
  ];

  const fetchItems = async () => {
    try {
      const res = await api.get<any>('/certificates/workplaces');
      setItems((res?.data ?? res) as WorkplaceRecord[]);
    } catch { setItems([]); }
  };

  useEffect(() => {
    const load = async () => { setLoading(true); await fetchItems(); setLoading(false); };
    load();
  }, []);

  const openNew = () => {
    setEditTarget(null);
    setDraft({ name: '', address: '', tel: '', position: '', occupation: '', department: '' });
    setShowForm(true);
  };

  const openEdit = (m: WorkplaceRecord) => {
    setEditTarget(m);
    setDraft({ name: m.name, address: m.address ?? '', tel: m.tel ?? '', position: m.position ?? '', occupation: m.occupation ?? '', department: m.department ?? '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { alert('名称は必須です'); return; }
    try {
      if (editTarget) {
        await api.put(`/certificates/workplaces/${editTarget.id}`, draft);
      } else {
        await api.post('/certificates/workplaces', draft);
      }
      setShowForm(false); setEditTarget(null);
      await fetchItems();
    } catch { alert('保存に失敗しました'); }
  };

  const handleDelete = async (m: WorkplaceRecord) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try { await api.del(`/certificates/workplaces/${m.id}`); await fetchItems(); }
    catch { alert('削除に失敗しました'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>乗船履歴証明書の勤務先（証明者の所属先）情報をマスタ登録します。</p>
        <button onClick={openNew}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#0057d9', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規追加
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="勤務先名称" k="name" s={s} />
              <SortTh label="所在地" k="address" s={s} />
              <SortTh label="電話番号" k="tel" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだ勤務先が登録されていません</td></tr>
            )}
            {s.rows.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>🏢 {m.name}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.address ?? '—'}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>{m.tel ?? '—'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(m)}
                      style={{ padding: '5px 12px', border: 'none', borderRadius: 7, background: '#3b82f6', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>編集</button>
                    <button onClick={() => handleDelete(m)}
                      style={{ padding: '5px 12px', border: 'none', borderRadius: 7, background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{editTarget ? '勤務先を編集' : '勤務先を新規追加'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {FIELDS.map(({ k, l }) => (
                <div key={k}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 700, color: '#374151' }}>{l}</label>
                  <input type="text" value={draft[k]} onChange={e => setDraft(p => ({ ...p, [k]: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={handleSave}
                style={{ flex: 2, padding: '11px 0', border: 'none', borderRadius: 10, background: '#0057d9', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>
                保存
              </button>
              <button onClick={() => { setShowForm(false); setEditTarget(null); }}
                style={{ flex: 1, padding: '11px 0', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#374151', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab: 証明書の入力候補マスタ
//   社内様式（船用品注文書・船員旅費精算書）で船員が選べる候補を登録する。
//   ここに無いものは船内で自由に書けるので、まず使うものだけ入れておけばよい。
// ─────────────────────────────────────────────────────────────────────────

interface CertOption { id: string; field: string; value: string; extra: string | null; sort_order: number }

/** 候補を登録できる項目。extraLabel がある項目は「選んだら一緒に入る値」を持てる */
const CERT_OPTION_FIELDS: { key: string; form: string; label: string; hint: string; extraLabel?: string }[] = [
  { key: 'supplierName', form: '船用品注文書', label: '注文先',       hint: '「○○ 御中」に出る取引先。よく使う船用品店を登録します。' },
  { key: 'itemName',     form: '船用品注文書', label: '品名',         hint: '注文品の候補。「既定の寸法」を入れておくと、品名を選んだとき寸法も一緒に入ります。', extraLabel: '既定の寸法' },
  { key: 'size',         form: '船用品注文書', label: '寸法',         hint: '「20L缶」「10kg」「Lサイズ」など、繰り返し使う単位。' },
  { key: 'remarks',      form: '船用品注文書', label: '備考',         hint: '銘柄・用途などの定型文。' },
  { key: 'loadPort',     form: '船用品注文書', label: '積込希望地',   hint: '寄港地マスタに登録した港は、ここに入れなくても自動で候補に出ます。' },
  { key: 'station',      form: '船員旅費精算書', label: '発駅・着駅', hint: 'よく使う駅・港・岸壁など。発駅と着駅で共通の候補になります。' },
  { key: 'transport',    form: '船員旅費精算書', label: '交通内容',   hint: '「電車」「新幹線」「タクシー」「高速バス」など。' },
  { key: 'jobTitle',     form: '船員旅費精算書', label: '職名',       hint: '役職マスタに登録した役職は、ここに入れなくても自動で候補に出ます。' },
];

function CertOptionMasterTab() {
  const [field, setField] = useState<string>('itemName');
  const [items, setItems] = useState<CertOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<CertOption | null>(null);
  const [draft, setDraft] = useState({ value: '', extra: '', sort_order: 0 });

  const meta = CERT_OPTION_FIELDS.find(f => f.key === field) ?? CERT_OPTION_FIELDS[0]!;
  const rows = items.filter(i => i.field === field).sort((a, b) => (a.sort_order - b.sort_order) || a.value.localeCompare(b.value, 'ja'));

  const fetchItems = async () => {
    try {
      const res = await api.get<any>('/cert-forms/options/manage');
      const data = (res as any)?.data ?? res;
      setItems(Array.isArray(data) ? data : []);
    } catch (err) { console.error('[CertOptionMasterTab]', err); }
    setLoading(false);
  };
  useEffect(() => { fetchItems(); }, []);

  const openNew  = () => { setDraft({ value: '', extra: '', sort_order: rows.length + 1 }); setEditTarget(null); setShowForm(true); };
  const openEdit = (m: CertOption) => { setDraft({ value: m.value, extra: m.extra ?? '', sort_order: m.sort_order }); setEditTarget(m); setShowForm(true); };

  const handleSave = async () => {
    if (!draft.value.trim()) { alert('候補を入力してください'); return; }
    try {
      if (editTarget) await api.put(`/cert-forms/options/${editTarget.id}`, draft);
      else            await api.post('/cert-forms/options', { ...draft, field });
      await fetchItems(); setShowForm(false); setEditTarget(null);
    } catch { alert('保存に失敗しました（同じ候補が既にあるかもしれません）'); }
  };
  const handleDelete = async (m: CertOption) => {
    if (!confirm(`「${m.value}」を候補から外しますか？\n（これまでに作った書類の内容は変わりません）`)) return;
    try { await api.del(`/cert-forms/options/${m.id}`); await fetchItems(); }
    catch { alert('削除に失敗しました'); }
  };

  const cell: React.CSSProperties = { padding: '8px 12px', border: '1px solid #e2e8f0' };

  return (
    <div>
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: '#92400e', lineHeight: 1.8, marginBottom: 16 }}>
        船員アプリと管理サイトの「証明書の作成」で、ここに登録した候補がプルダウンに出ます。<br />
        候補に無いものは船内でそのまま自由に入力できるので、よく使うものだけ登録しておけば十分です。
      </div>

      {/* 項目の切り替え（様式ごとにまとめて表示） */}
      {['船用品注文書', '船員旅費精算書'].map(formLabel => (
        <div key={formLabel} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5 }}>{formLabel}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {CERT_OPTION_FIELDS.filter(f => f.form === formLabel).map(f => {
              const active = field === f.key;
              const n = items.filter(i => i.field === f.key).length;
              return (
                <button key={f.key} onClick={() => setField(f.key)}
                  style={{ padding: '6px 14px', borderRadius: 999, border: active ? '2px solid #d97706' : '1px solid #e2e8f0',
                    background: active ? '#fffbeb' : '#fff', color: active ? '#b45309' : '#475569',
                    fontSize: 12.5, fontWeight: active ? 800 : 600, cursor: 'pointer' }}>
                  {f.label}{n > 0 && <span style={{ marginLeft: 6, color: '#94a3b8', fontWeight: 600 }}>{n}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', margin: '18px 0 12px' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{meta.label}の候補</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{meta.hint}</div>
        </div>
        <button onClick={openNew}
          style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: '#0057d9', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>
          ＋ 新規登録
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ ...cell, textAlign: 'left' }}>候補</th>
            {meta.extraLabel && <th style={{ ...cell, textAlign: 'left', width: 200 }}>{meta.extraLabel}</th>}
            <th style={{ ...cell, textAlign: 'center', width: 80 }}>順序</th>
            <th style={{ ...cell, textAlign: 'center', width: 140 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(m => (
            <tr key={m.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={cell}>{m.value}</td>
              {meta.extraLabel && <td style={{ ...cell, color: m.extra ? '#0f172a' : '#cbd5e1' }}>{m.extra || '—'}</td>}
              <td style={{ ...cell, textAlign: 'center' }}>{m.sort_order}</td>
              <td style={{ ...cell, textAlign: 'center' }}>
                <button onClick={() => openEdit(m)} style={{ marginRight: 8, padding: '4px 12px', border: '1px solid #0057d9', borderRadius: 6, background: '#fff', color: '#0057d9', cursor: 'pointer', fontSize: 12 }}>編集</button>
                <button onClick={() => handleDelete(m)} style={{ padding: '4px 12px', border: '1px solid #ef4444', borderRadius: 6, background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>削除</button>
              </td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={meta.extraLabel ? 4 : 3} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>
              登録がありません（船内では自由入力になります）
            </td></tr>
          )}
        </tbody>
      </table>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 400 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{editTarget ? `${meta.label}の候補を編集` : `${meta.label}の候補を追加`}</div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 18 }}>{meta.form}</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>候補</label>
              <input value={draft.value} onChange={e => setDraft(p => ({ ...p, value: e.target.value }))} autoFocus
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            {meta.extraLabel && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>{meta.extraLabel}（任意）</label>
                <input value={draft.extra} onChange={e => setDraft(p => ({ ...p, extra: e.target.value }))} placeholder="例: 20L缶"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>表示順序</label>
              <input type="number" value={draft.sort_order} onChange={e => setDraft(p => ({ ...p, sort_order: Number(e.target.value) }))}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={handleSave} style={{ flex: 2, padding: '11px 0', border: 'none', borderRadius: 10, background: '#0057d9', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>保存</button>
              <button onClick={() => { setShowForm(false); setEditTarget(null); }} style={{ flex: 1, padding: '11px 0', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#374151', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCategoryMasterTab() {
  const [items, setItems] = useState<any[]>([]);
  const s = useSimpleSort<any>(items, { name: m => m.name, sort_order: m => m.sort_order }, 'sort_order');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [draft, setDraft] = useState({ name: '', sort_order: 0 });

  const fetchItems = async () => {
    const res = await api.get<any>('/certificates/report-categories');
    const data = (res as any)?.data ?? res;
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => { fetchItems(); }, []);

  const openNew  = () => { setDraft({ name: '', sort_order: items.length + 1 }); setEditTarget(null); setShowForm(true); };
  const openEdit = (m: any) => { setDraft({ name: m.name, sort_order: m.sort_order }); setEditTarget(m); setShowForm(true); };

  const handleSave = async () => {
    if (!draft.name.trim()) { alert('区分名を入力してください'); return; }
    if (editTarget) await api.put(`/certificates/report-categories/${editTarget.id}`, draft);
    else            await api.post('/certificates/report-categories', draft);
    await fetchItems(); setShowForm(false); setEditTarget(null);
  };
  const handleDelete = async (m: any) => {
    if (!confirm(`「${m.name}」を削除しますか？`)) return;
    try { await api.del(`/certificates/report-categories/${m.id}`); await fetchItems(); }
    catch { alert('削除に失敗しました'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>届出区分マスタ</div>
        <button onClick={openNew}
          style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: '#0057d9', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          ＋ 新規登録
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <SortTh label="区分名" k="name" s={s} />
            <SortTh label="順序" k="sort_order" s={s} align="center" />
            <th style={{ padding: '8px 12px', textAlign: 'center', border: '1px solid #e2e8f0', width: 120 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {s.rows.map(m => (
            <tr key={m.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{m.name}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center', border: '1px solid #e2e8f0' }}>{m.sort_order}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <button onClick={() => openEdit(m)} style={{ marginRight: 8, padding: '4px 12px', border: '1px solid #0057d9', borderRadius: 6, background: '#fff', color: '#0057d9', cursor: 'pointer', fontSize: 12 }}>編集</button>
                <button onClick={() => handleDelete(m)} style={{ padding: '4px 12px', border: '1px solid #ef4444', borderRadius: 6, background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>削除</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={3} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>登録がありません</td></tr>}
        </tbody>
      </table>
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 360 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 18 }}>{editTarget ? '届出区分を編集' : '届出区分を新規登録'}</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>区分名</label>
              <input value={draft.name} onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>表示順序</label>
              <input type="number" value={draft.sort_order} onChange={e => setDraft(p => ({ ...p, sort_order: Number(e.target.value) }))}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={handleSave} style={{ flex: 2, padding: '11px 0', border: 'none', borderRadius: 10, background: '#0057d9', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>保存</button>
              <button onClick={() => { setShowForm(false); setEditTarget(null); }} style={{ flex: 1, padding: '11px 0', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#374151', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab: ヒヤリハットカテゴリマスタ
// ═══════════════════════════════════════════════════════════════════════════
function IncidentCategoryMasterTab() {
  const [masters, setMasters] = useState<IncidentCategoryMaster[]>([]);
  const s = useSimpleSort<IncidentCategoryMaster>(masters, { name: m => m.name, display_order: m => m.display_order, is_active: m => m.is_active }, 'display_order');
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<IncidentCategoryMaster | null>(null);
  const [showNew, setShowNew] = useState(false);

  const fetchMasters = async () => {
    try {
      const res = await api.get<any>('/incidents/categories');
      setMasters(res?.data ?? []);
    } catch (err) { console.error('[ActivityMasterPage]', err); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get<any>('/incidents/categories');
        setMasters(res?.data ?? []);
      } catch (err) { console.error('[ActivityMasterPage]', err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleDelete = async (m: IncidentCategoryMaster) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try {
      await api.del(`/incidents/categories/${m.id}`);
      fetchMasters();
    } catch (err: any) {
      alert(err?.message ?? '削除に失敗しました');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div>
      {/* 新規追加ボタン */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowNew(true)}
          style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          ＋ 新規登録
        </button>
      </div>

      {/* マスタ一覧 */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 32 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <SortTh label="名称" k="name" s={s} />
              <SortTh label="色" />
              <SortTh label="並び順" k="display_order" s={s} />
              <SortTh label="状態" k="is_active" s={s} />
              <SortTh label="操作" />
            </tr>
          </thead>
          <tbody>
            {masters.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>まだヒヤリハットカテゴリが登録されていません</td></tr>
            )}
            {s.rows.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{m.name}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: m.color }} />
                </td>
                <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>{m.display_order}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.is_active ? '#f0fdf4' : '#f8fafc', color: m.is_active ? '#059669' : '#94a3b8', border: `1px solid ${m.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {m.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditTarget(m)}
                      style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 600 }}>編集</button>
                    <button onClick={async () => {
                      await api.put(`/incidents/categories/${m.id}`, { is_active: m.is_active ? 0 : 1 });
                      fetchMasters();
                    }} style={{ padding: '5px 12px', border: `1px solid ${m.is_active ? '#fecaca' : '#bbf7d0'}`, borderRadius: 6, background: m.is_active ? '#fef2f2' : '#f0fdf4', cursor: 'pointer', fontSize: 12, color: m.is_active ? '#dc2626' : '#059669', fontWeight: 600 }}>
                      {m.is_active ? '無効化' : '有効化'}
                    </button>
                    <button onClick={() => handleDelete(m)}
                      style={{ padding: '5px 12px', border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* モーダル類 */}
      {showNew && <IncidentCategoryMasterFormModal mode="create" onSaved={() => { setShowNew(false); fetchMasters(); }} onClose={() => setShowNew(false)} />}
      {editTarget && <IncidentCategoryMasterFormModal mode="edit" initial={editTarget} onSaved={() => { setEditTarget(null); fetchMasters(); }} onClose={() => setEditTarget(null)} />}
    </div>
  );
}

// ─── IncidentCategoryMasterFormModal ───────────────────────────────────────
function IncidentCategoryMasterFormModal({ mode, initial, onSaved, onClose }: {
  mode: 'create' | 'edit';
  initial?: IncidentCategoryMaster;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name,          setName]         = useState(initial?.name ?? '');
  const [color,         setColor]        = useState(initial?.color ?? '#dc2626');
  const [displayOrder,  setDisplayOrder] = useState(initial?.display_order ?? 0);
  const [error,         setError]        = useState('');
  const [saving,        setSaving]       = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { setError('名称を入力してください'); return; }
    setSaving(true); setError('');
    try {
      if (mode === 'create') {
        await api.post('/incidents/categories', { name, color });
      } else if (initial) {
        await api.put(`/incidents/categories/${initial.id}`, { name, color, display_order: displayOrder });
      }
      onSaved();
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 440, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{mode === 'create' ? 'ヒヤリハットカテゴリを追加' : 'ヒヤリハットカテゴリを編集'}</div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
          名称（必須）
          <input value={name} onChange={e => setName(e.target.value)} placeholder="例：滑り・転倒"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none' }} />
        </label>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>カラー</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)}
                style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: color === c ? '3px solid #0f172a' : '2px solid transparent', cursor: 'pointer' }} />
            ))}
          </div>
        </div>

        {mode === 'edit' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 18 }}>
            並び順
            <input type="number" value={displayOrder} onChange={e => setDisplayOrder(Number(e.target.value))}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none' }} />
          </label>
        )}

        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#93c5fd' : '#dc2626', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '保存中...' : mode === 'create' ? '追加する' : '更新する'}
          </button>
        </div>
      </div>
    </div>
  );
}